/* ============================================================
   Cadouri Superbe — theme.js
   All interactions. No dependencies. Every init is isolated
   in try/catch so one failure never breaks the page.
   ============================================================ */
(function () {
  'use strict';

  var T = window.theme || {};
  var strings = T.strings || {};
  var routes = T.routes || {};
  var isDesignMode = Boolean(T.designMode || (window.Shopify && window.Shopify.designMode));

  /* ---------- utilities ---------- */

  function esc(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function formatMoney(cents) {
    // Mirrors snippets/price.liquid: when the merchant enables the currency code,
    // Liquid renders `money_with_currency` — JS must use the same format or the
    // PDP price would silently lose the code after a variant change.
    var format = (T.currencyCodeEnabled && T.moneyWithCurrencyFormat) ? T.moneyWithCurrencyFormat : (T.moneyFormat || '{{amount}}');
    var value = (cents || 0) / 100;
    function num(dec, decSep, thouSep) {
      var fixed = value.toFixed(dec);
      var parts = fixed.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thouSep);
      return dec ? parts.join(decSep) : parts[0];
    }
    return format
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, num(2, ',', '.'))
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, num(0, ',', '.'))
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, num(0, '.', ','))
      .replace(/\{\{\s*amount\s*\}\}/, num(2, '.', ','));
  }

  // /search/suggest.json and /collections/x/products.json quote prices as
  // decimal strings ("49.00"); /cart.js and /products/x.js use integer subunits.
  // Everything is normalised to subunits so a single formatMoney() call keeps
  // JS-rendered prices identical to Liquid's `| money` output.
  function toSubunits(value, alreadySubunits) {
    if (value == null || value === '') return null;
    if (alreadySubunits) {
      var whole = parseInt(value, 10);
      return isNaN(whole) ? null : whole;
    }
    var decimal = parseFloat(value);
    return isNaN(decimal) ? null : Math.round(decimal * 100);
  }

  function fetchJSON(url, options) {
    return fetch(url, options).then(function (r) {
      if (!r.ok) throw new Error('Request failed: ' + r.status);
      return r.json();
    });
  }

  // Shopify CDN URLs coming back from /cart.js and /products/x.js are unsized
  // originals. Liquid templates always request a transform, so JS-injected
  // images have to do the same or they pull multi-megabyte files.
  function sizedImage(url, width) {
    if (!url) return '';
    var src = String(url);
    if (src.indexOf('//') === 0) src = 'https:' + src;
    if (/[?&]width=/.test(src)) return src;
    return src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  function dispatchThemeEvent(name, detail) {
    try {
      document.dispatchEvent(new CustomEvent(name, { detail: detail, bubbles: true }));
    } catch (e) { /* tracking must never break a shopping flow */ }
  }

  function rooted(path) {
    var root = routes.root || '/';
    return root.replace(/\/?$/, '/') + String(path || '').replace(/^\//, '');
  }

  function onReady(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  // An element is only a real tab stop when it is actually rendered. offsetParent
  // is null for position:fixed containers (the drawers/modal), so the
  // getClientRects() fallback is what filters `hidden`/display:none branches out.
  function isFocusVisible(el) {
    if (el.disabled) return false;
    return Boolean(el.offsetParent !== null || el.getClientRects().length);
  }

  function trapFocus(container, e) {
    var focusables = Array.prototype.filter.call(
      container.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])'
      ),
      isFocusVisible
    );
    if (!focusables.length) return;
    var first = focusables[0];
    var last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  function setDialogState(element, open) {
    if (!element) return;
    element.setAttribute('aria-hidden', open ? 'false' : 'true');
    if ('inert' in element) element.inert = !open;
    else if (open) element.removeAttribute('inert');
    else element.setAttribute('inert', '');
  }

  /* ---------- overlay + scroll lock ---------- */

  var Overlay = {
    el: null,
    active: null,
    ensure: function () {
      if (!this.el) {
        this.el = document.createElement('div');
        this.el.className = 'overlay';
        document.body.appendChild(this.el);
        var self = this;
        this.el.addEventListener('click', function () {
          if (self.active && typeof self.active.close === 'function') self.active.close();
        });
      }
      return this.el;
    },
    show: function (owner) {
      this.ensure().classList.add('is-visible');
      this.active = owner;
      document.body.classList.add('no-scroll');
    },
    hide: function () {
      if (this.el) this.el.classList.remove('is-visible');
      this.active = null;
      document.body.classList.remove('no-scroll');
    }
  };

  /* ---------- header ---------- */

  function initHeader() {
    var header = document.querySelector('.header');
    if (!header) return;
    if (header.getAttribute('data-header-bound') === 'true') return;
    header.setAttribute('data-header-bound', 'true');
    var onScroll = function () {
      header.classList.toggle('is-scrolled', window.scrollY > 10);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  function initDesktopMenus() {
    var menus = Array.prototype.slice.call(document.querySelectorAll('[data-desktop-menu]'));
    if (!menus.length) return;

    function closeAll(except) {
      document.querySelectorAll('[data-desktop-menu]').forEach(function (details) {
        if (details !== except && details.open) details.open = false;
      });
    }

    var hoverCapable = window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches;

    menus.forEach(function (details) {
      if (details.getAttribute('data-desktop-menu-bound') === 'true') return;
      details.setAttribute('data-desktop-menu-bound', 'true');
      var summary = details.querySelector('summary');
      if (summary) summary.setAttribute('aria-expanded', String(details.open));
      details.addEventListener('toggle', function () {
        if (summary) summary.setAttribute('aria-expanded', String(details.open));
        if (details.open) closeAll(details);
      });

      // Mouse users get hover-open with a small intent delay; touch and
      // keyboard keep the native click/Enter toggle. A click right after a
      // hover-open would natively re-toggle the menu closed, so that first
      // click is swallowed.
      if (hoverCapable) {
        var openTimer = null;
        var closeTimer = null;
        var hoverOpenedAt = 0;
        details.addEventListener('mouseenter', function () {
          window.clearTimeout(closeTimer);
          if (details.open) return;
          openTimer = window.setTimeout(function () {
            details.open = true;
            hoverOpenedAt = Date.now();
          }, 60);
        });
        details.addEventListener('mouseleave', function () {
          window.clearTimeout(openTimer);
          closeTimer = window.setTimeout(function () {
            details.open = false;
          }, 160);
        });
        if (summary) {
          summary.addEventListener('click', function (e) {
            if (details.open && Date.now() - hoverOpenedAt < 600) {
              e.preventDefault();
              hoverOpenedAt = 0;
            }
          });
        }
      }
    });

    if (document.documentElement.getAttribute('data-desktop-menu-global-bound') !== 'true') {
      document.documentElement.setAttribute('data-desktop-menu-global-bound', 'true');
      document.addEventListener('click', function (e) {
        if (!e.target.closest('[data-desktop-nav]')) closeAll();
      });
      document.addEventListener('keydown', function (e) {
        if (e.key !== 'Escape') return;
        var openMenu = document.querySelector('[data-desktop-menu][open]');
        if (!openMenu) return;
        var summary = openMenu.querySelector('summary');
        openMenu.open = false;
        if (summary) summary.focus();
      });
      if (window.matchMedia) {
        var desktopBreakpoint = window.matchMedia('(max-width: 1099px)');
        if (desktopBreakpoint.addEventListener) {
          desktopBreakpoint.addEventListener('change', function () { closeAll(); });
        } else if (desktopBreakpoint.addListener) {
          desktopBreakpoint.addListener(function () { closeAll(); });
        }
      }
    }
  }

  /* ---------- announcement bar ---------- */

  var store = {
    get: function (fn, key) {
      try { return window[fn].getItem(key); } catch (e) { return null; }
    },
    set: function (fn, key, val) {
      try { window[fn].setItem(key, val); } catch (e) { /* sandboxed */ }
    }
  };

  function initAnnouncement() {
    var bar = document.querySelector('[data-announcement]');
    if (!bar) return;
    if (bar.getAttribute('data-announcement-bound') === 'true') return;
    bar.setAttribute('data-announcement-bound', 'true');
    var messages = bar.querySelectorAll('.announcement__item');
    var closeBtn = bar.querySelector('[data-announcement-close]');

    if (store.get('sessionStorage', 'cs-announce-closed') === '1') {
      bar.style.display = 'none';
      return;
    }
    if (closeBtn) {
      closeBtn.addEventListener('click', function () {
        bar.style.display = 'none';
        store.set('sessionStorage', 'cs-announce-closed', '1');
      });
    }
    var reduceMotion = window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (messages.length > 1 && !reduceMotion && !isDesignMode) {
      // Only the .is-active message is rendered (base.css hides the rest), so
      // without a live region assistive tech never hears messages 2..n.
      bar.setAttribute('aria-live', 'polite');
      bar.setAttribute('aria-atomic', 'true');
      var i = 0;
      bar._csTimer = window.setInterval(function () {
        messages[i].classList.remove('is-active');
        i = (i + 1) % messages.length;
        messages[i].classList.add('is-active');
      }, 5000);
    }
  }

  /* ---------- mobile nav ---------- */

  function initMobileNav() {
    var nav = document.querySelector('.mobile-nav');
    var openBtn = document.querySelector('[data-open-nav]');
    if (!nav || !openBtn) return;
    if (nav.getAttribute('data-mobile-nav-bound') === 'true') return;
    nav.setAttribute('data-mobile-nav-bound', 'true');
    var closeBtns = nav.querySelectorAll('[data-close-nav]');
    var panels = Array.prototype.slice.call(nav.querySelectorAll('[data-mobile-panel]'));
    var rootPanel = nav.querySelector('[data-mobile-panel="root"]');

    function cancelHide(panel) {
      if (panel && panel._csHideTimer) {
        window.clearTimeout(panel._csHideTimer);
        panel._csHideTimer = null;
      }
    }

    function resetPanels() {
      panels.forEach(function (panel) {
        var isRoot = panel === rootPanel;
        cancelHide(panel);
        panel.classList.toggle('is-active', isRoot);
        panel.classList.remove('is-behind');
        panel.hidden = !isRoot;
        if ('inert' in panel) panel.inert = !isRoot;
        else if (isRoot) panel.removeAttribute('inert');
        else panel.setAttribute('inert', '');
      });
    }

    function showPanel(name) {
      var current = nav.querySelector('[data-mobile-panel].is-active');
      var target = nav.querySelector('[data-mobile-panel="' + name + '"]');
      if (!current || !target || current === target) return;
      // A pending hide from showRoot() would otherwise fire after this panel is
      // re-opened and leave an empty drawer.
      cancelHide(target);
      current.classList.remove('is-active');
      current.classList.add('is-behind');
      if ('inert' in current) current.inert = true;
      else current.setAttribute('inert', '');
      target.hidden = false;
      if ('inert' in target) target.inert = false;
      else target.removeAttribute('inert');
      window.requestAnimationFrame(function () {
        target.classList.add('is-active');
        var back = target.querySelector('[data-mobile-back]');
        if (back) back.focus();
      });
    }

    function showRoot() {
      var current = nav.querySelector('[data-mobile-panel].is-active');
      if (!rootPanel || !current || current === rootPanel) return;
      current.classList.remove('is-active');
      if ('inert' in current) current.inert = true;
      else current.setAttribute('inert', '');
      rootPanel.hidden = false;
      rootPanel.classList.remove('is-behind');
      if ('inert' in rootPanel) rootPanel.inert = false;
      else rootPanel.removeAttribute('inert');
      window.requestAnimationFrame(function () {
        rootPanel.classList.add('is-active');
        var trigger = rootPanel.querySelector('[data-mobile-go="' + current.getAttribute('data-mobile-panel') + '"]');
        if (trigger) trigger.focus();
        cancelHide(current);
        current._csHideTimer = window.setTimeout(function () {
          current._csHideTimer = null;
          current.hidden = true;
        }, 330);
      });
    }

    var api = {
      close: function () {
        nav.classList.remove('is-open');
        setDialogState(nav, false);
        Overlay.hide();
        openBtn.setAttribute('aria-expanded', 'false');
        resetPanels();
        openBtn.focus();
      }
    };
    openBtn.addEventListener('click', function () {
      resetPanels();
      setDialogState(nav, true);
      nav.classList.add('is-open');
      Overlay.show(api);
      openBtn.setAttribute('aria-expanded', 'true');
      var closeButton = nav.querySelector('[data-close-nav]');
      if (closeButton) closeButton.focus();
    });
    closeBtns.forEach(function (b) { b.addEventListener('click', api.close); });
    nav.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') api.close();
      if (e.key === 'Tab') trapFocus(nav, e);
    });
    nav.querySelectorAll('[data-mobile-go]').forEach(function (button) {
      button.addEventListener('click', function () { showPanel(button.getAttribute('data-mobile-go')); });
    });
    nav.querySelectorAll('[data-mobile-back]').forEach(function (button) {
      button.addEventListener('click', showRoot);
    });

    // expandable submenus
    nav.querySelectorAll('[data-nav-toggle]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var target = document.getElementById(btn.getAttribute('aria-controls'));
        var expanded = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', String(!expanded));
        if (target) target.hidden = expanded;
      });
    });
  }

  /* ---------- product card fragments shared by JS-rendered surfaces ---------- */

  // Every surface that builds product cards in JS (search modal, recently
  // viewed) clones the quick-add prototypes its own section renders, so the
  // button element and its icon come from snippets/product-card.liquid's markup
  // instead of a second copy that would silently drift.
  function readCardTemplates(scope) {
    function html(selector) {
      var node = scope.querySelector(selector);
      return node ? node.innerHTML : '';
    }
    return {
      addForm: html('[data-rv-add-form], [data-search-add-form]'),
      addLink: html('[data-rv-add-link], [data-search-add-link]'),
      soldOut: html('[data-rv-badge-soldout]'),
      sale: html('[data-rv-badge-sale]'),
      isNew: html('[data-rv-badge-new]'),
      mediaFallback: html('[data-rv-media-fallback]'),
      history: html('[data-search-icon-history]')
    };
  }

  // Replacement values go through a function so a `$&` sequence inside a title
  // or a money format is never read back as a regexp replacement pattern.
  function fillTokens(template, tokens) {
    var out = String(template || '');
    Object.keys(tokens).forEach(function (token) {
      out = out.replace(token, function () { return esc(tokens[token]); });
    });
    return out;
  }

  // Mirrors the availability / variant branching in snippets/product-card.liquid:
  // sold out renders nothing, a single variant adds straight to the cart, a real
  // choice links to the PDP. When the variant id is not in the payload the form
  // is rendered without one and initProductForms resolves it on first click.
  function quickAddHTML(templates, item) {
    if (!item || !item.available) return '';
    if (item.quickAddMode === 'options') {
      if (!templates.addLink) return '';
      return fillTokens(templates.addLink, { '__URL__': item.url, '__LABEL__': item.optionsLabel });
    }
    if (!templates.addForm) return '';
    return fillTokens(templates.addForm, {
      '__VARIANT_ID__': item.variantId == null ? '' : item.variantId,
      '__HANDLE__': item.handle,
      '__LABEL__': item.addLabel
    });
  }

  // /search/suggest.json, /collections/x/products.json and /products/x.js each
  // describe a product differently (price unit, image shape, availability).
  // Normalising once keeps a single card builder for all three.
  // source: 'suggest' | 'collection' | 'product'
  function normalizeProduct(raw, source) {
    if (!raw) return null;
    var subunits = source === 'product';
    var variants = raw.variants || [];
    var images = raw.images || [];
    var first = variants[0] || null;

    function imageAt(index) {
      var entry = images[index];
      if (!entry) return '';
      return entry.src || (typeof entry === 'string' ? entry : '') || '';
    }

    var image = '';
    if (raw.featured_image && raw.featured_image.url) image = raw.featured_image.url;
    else if (typeof raw.featured_image === 'string') image = raw.featured_image;
    else if (typeof raw.image === 'string') image = raw.image;
    else image = imageAt(0);

    var available = raw.available;
    if (available == null) {
      available = false;
      for (var i = 0; i < variants.length; i++) {
        if (variants[i] && variants[i].available !== false) { available = true; break; }
      }
    }
    available = available !== false;

    var price = raw.price;
    if (price == null && first) price = first.price;
    var compare = raw.compare_at_price;
    if (compare == null && first) compare = first.compare_at_price;
    var priceValue = toSubunits(price, subunits);
    var compareValue = toSubunits(compare, subunits);

    // /search/suggest.json omits the variants array entirely for products that
    // only have the default variant and populates it only for products with a
    // real choice. So an empty array means "one buyable variant whose id is not
    // in this payload", never "nothing to add" — those get a quick-add button
    // that resolves its variant from /products/<handle>.js on the first click.
    // /collections/x/products.json and /products/x.js always carry the full
    // array, so they never take the lazy path.
    var handle = raw.handle || '';
    var quickAddMode = 'add';
    if (variants.length > 1) quickAddMode = 'options';
    else if (variants.length === 0 && !handle) quickAddMode = 'options';

    var title = raw.title || '';
    return {
      handle: handle,
      // Judge.me keys its preview badges off the numeric product id, so the
      // client-rendered cards need it to reproduce the server-rendered rating.
      id: raw.id || null,
      title: title,
      url: raw.url || (handle ? rooted('products/' + encodeURIComponent(handle)) : '#'),
      image: image || '',
      hoverImage: imageAt(1),
      vendor: raw.vendor || '',
      price: priceValue,
      compare: (compareValue != null && priceValue != null && compareValue > priceValue) ? compareValue : null,
      available: available,
      publishedAt: raw.published_at || raw.created_at || '',
      quickAddMode: quickAddMode,
      variantId: (available && variants.length === 1 && first && first.id) ? first.id : null,
      addLabel: (strings.addToCart || 'Adaugă în coș') + ' – ' + title,
      optionsLabel: (strings.chooseOptions || 'Alege opțiuni') + ' – ' + title
    };
  }

  /* ---------- predictive search (enhanced production search) ---------- */

  // resources[limit] below. When the API returns exactly this many products the
  // real result set is larger, which is what switches the footer label.
  var SEARCH_LIMIT = 10;
  var SEARCH_RECENT_KEY = 'cs-recent-searches';
  var SEARCH_RECENT_MAX = 5;
  var SEARCH_BESTSELLER_KEY = 'cs-search-bestsellers';

  function initSearch() {
    var modal = document.getElementById('SearchModal');
    if (!modal) return;

    var input = modal.querySelector('[data-search-input]');
    var form = modal.querySelector('[data-search-form]');
    var resultsBox = modal.querySelector('[data-search-results]');
    var initialBox = modal.querySelector('[data-search-initial]');
    var footer = modal.querySelector('[data-search-footer]');
    var countEl = modal.querySelector('[data-search-count]');
    var viewAll = modal.querySelector('[data-search-viewall]');
    var liveRegion = modal.querySelector('[data-search-live]');
    var clearBtn = modal.querySelector('[data-search-clear]');
    var recentBox = modal.querySelector('[data-search-recent]');
    var recentList = modal.querySelector('[data-search-recent-list]');
    var recentLabel = modal.querySelector('[data-search-recent-label]');
    var recentClear = modal.querySelector('[data-search-recent-clear]');
    var closers = modal.querySelectorAll('[data-search-close]');
    var templates = readCardTemplates(modal);
    var lastFocused = null;
    var debounceTimer = null;
    var abortCtrl = null;
    var activeIndex = -1;
    var itemSeq = 0;

    var api = {
      close: function () {
        if (abortCtrl) abortCtrl.abort();
        modal.classList.remove('is-open');
        setDialogState(modal, false);
        Overlay.hide();
        if (lastFocused) lastFocused.focus();
      }
    };

    function open() {
      lastFocused = document.activeElement;
      setDialogState(modal, true);
      modal.classList.add('is-open');
      Overlay.show(api);
      setTimeout(function () { input && input.focus(); }, 60);
    }

    // Keep the first set of closures — they are the ones the modal's own
    // listeners were wired with.
    if (!modal._csSearchApi) modal._csSearchApi = { open: open, close: api.close };

    // The openers live in sections/header.liquid, a different section from the
    // modal, so they must be guarded per element — otherwise reloading either
    // section in the editor leaves a dead or double-bound search icon.
    document.querySelectorAll('[data-open-search]').forEach(function (b) {
      if (b.getAttribute('data-search-opener-bound') === 'true') return;
      b.setAttribute('data-search-opener-bound', 'true');
      b.addEventListener('click', function () {
        var live = document.getElementById('SearchModal');
        if (live && live._csSearchApi) live._csSearchApi.open();
      });
    });

    // Adding from a result card opens the cart drawer. Guarded on the document
    // so an editor section reload cannot stack duplicate listeners, and it
    // re-resolves the live modal instead of closing over a detached one.
    if (!document._csSearchAtcBound) {
      document._csSearchAtcBound = true;
      document.addEventListener('cs:add-to-cart', function () {
        var live = document.getElementById('SearchModal');
        if (live && live.classList.contains('is-open') && live._csSearchApi) live._csSearchApi.close();
      });
    }

    if (modal.getAttribute('data-search-bound') === 'true') return;
    modal.setAttribute('data-search-bound', 'true');

    if (input) {
      input.setAttribute('role', 'combobox');
      input.setAttribute('aria-autocomplete', 'list');
      input.setAttribute('aria-expanded', 'false');
    }
    if (resultsBox) resultsBox.setAttribute('role', 'listbox');

    closers.forEach(function (b) { b.addEventListener('click', api.close); });
    modal.addEventListener('click', function (e) {
      if (e.target === modal) api.close();
    });
    modal.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') api.close();
      if (e.key === 'Tab') {
        // Leaving the combobox: drop the virtual selection so real focus and
        // aria-activedescendant can never disagree about the active option.
        clearActive();
        trapFocus(modal, e);
      }
    });

    /* --- recent searches (localStorage) --- */

    function readRecent() {
      try {
        var stored = JSON.parse(localStorage.getItem(SEARCH_RECENT_KEY) || '[]');
        if (Object.prototype.toString.call(stored) !== '[object Array]') return [];
        return stored.filter(function (term) {
          return typeof term === 'string' && term.length > 0;
        }).slice(0, SEARCH_RECENT_MAX);
      } catch (e) { return []; }
    }

    function saveRecent(term) {
      var clean = String(term == null ? '' : term).trim();
      if (clean.length < 2) return;
      var lower = clean.toLowerCase();
      var list = readRecent().filter(function (entry) { return entry.toLowerCase() !== lower; });
      list.unshift(clean);
      try {
        localStorage.setItem(SEARCH_RECENT_KEY, JSON.stringify(list.slice(0, SEARCH_RECENT_MAX)));
      } catch (e) { /* private mode / quota — history is optional */ }
      renderRecent();
    }

    function renderRecent() {
      if (!recentBox || !recentList) return;
      var list = readRecent();
      recentBox.hidden = list.length === 0;
      if (recentLabel) recentLabel.textContent = strings.searchRecent || 'Căutări recente';
      if (recentClear) {
        recentClear.textContent = strings.searchClearRecent || 'Șterge istoricul';
      }
      recentList.innerHTML = list.map(function (term) {
        return '<button type="button" class="search-chip search-chip--recent" data-search-recent-term="' +
          esc(term) + '">' + templates.history + '<span>' + esc(term) + '</span></button>';
      }).join('');
    }

    if (recentClear) {
      recentClear.addEventListener('click', function () {
        try { localStorage.removeItem(SEARCH_RECENT_KEY); } catch (e) { /* nothing to clear */ }
        renderRecent();
        if (input) input.focus();
      });
    }

    if (recentList) {
      recentList.addEventListener('click', function (e) {
        var chip = e.target.closest('[data-search-recent-term]');
        if (!chip) return;
        runQuery(chip.getAttribute('data-search-recent-term') || '');
      });
    }

    /* --- "Populare acum": one live fetch per session --- */

    function paintBestsellers(box, list) {
      var html = list.slice(0, 4).map(function (p) {
        return searchCard(p, '', 'collection', false);
      }).join('');
      if (!html) return;
      box.innerHTML = html;
      initProductForms();
    }

    function initBestsellers() {
      var box = modal.querySelector('[data-search-bestsellers]');
      if (!box) return;
      if (isDesignMode) {
        // Editor safe mode: the server-rendered fallback cards stay as they are.
        box.setAttribute('data-editor-network-paused', 'true');
        return;
      }
      var handle = box.getAttribute('data-collection');
      if (!handle) return;

      var cached = null;
      try { cached = JSON.parse(sessionStorage.getItem(SEARCH_BESTSELLER_KEY) || 'null'); }
      catch (e) { cached = null; }
      if (cached && cached.handle === handle && cached.products && cached.products.length) {
        paintBestsellers(box, cached.products);
        return;
      }

      fetchJSON(rooted('collections/' + encodeURIComponent(handle) + '/products.json?limit=4'))
        .then(function (data) {
          var list = (data && data.products) || [];
          if (!list.length) return;
          // Only the fields the card needs are cached; the raw payload carries
          // full body_html for every product.
          var slim = list.slice(0, 4).map(function (p) {
            return {
              title: p.title,
              handle: p.handle,
              vendor: p.vendor,
              images: (p.images || []).slice(0, 1).map(function (img) { return { src: img.src }; }),
              variants: (p.variants || []).map(function (v) {
                return { id: v.id, price: v.price, compare_at_price: v.compare_at_price, available: v.available };
              })
            };
          });
          try { sessionStorage.setItem(SEARCH_BESTSELLER_KEY, JSON.stringify({ handle: handle, products: slim })); }
          catch (e) { /* cache is an optimisation, not a requirement */ }
          paintBestsellers(box, slim);
        })
        .catch(function () { /* the server-rendered fallback cards remain */ });
    }

    /* --- results --- */

    function showInitial() {
      // An in-flight request must be cancelled here, otherwise it resolves into
      // render() and puts a stale product grid back over the cleared query.
      if (abortCtrl) { abortCtrl.abort(); abortCtrl = null; }
      if (initialBox) initialBox.hidden = false;
      if (resultsBox) { resultsBox.hidden = true; resultsBox.innerHTML = ''; }
      if (footer) footer.hidden = true;
      if (viewAll) viewAll.classList.remove('is-visible', 'is-active');
      if (input) {
        input.setAttribute('aria-expanded', 'false');
        input.removeAttribute('aria-activedescendant');
      }
      activeIndex = -1;
    }

    function announce(msg) {
      if (liveRegion) liveRegion.textContent = msg;
    }

    // Match on the raw text and escape each segment afterwards. Escaping first
    // and then running the regex corrupts entities (a query of "amp" against a
    // title containing "&" would render the literal text "&amp;").
    function highlight(text, terms) {
      var raw = String(text == null ? '' : text);
      var t = String(terms == null ? '' : terms).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      if (!t) return esc(raw);
      var re;
      try { re = new RegExp(t, 'ig'); } catch (e) { return esc(raw); }
      var out = '';
      var cursor = 0;
      var match;
      while ((match = re.exec(raw)) !== null) {
        if (match[0] === '') { re.lastIndex++; continue; }
        out += esc(raw.slice(cursor, match.index)) + '<mark>' + esc(match[0]) + '</mark>';
        cursor = match.index + match[0].length;
      }
      return out + esc(raw.slice(cursor));
    }

    // Mirrors the two-zone result layout so the loading state has the shape of
    // the content that replaces it instead of a generic block of tiles.
    function skeletons() {
      var chips = '';
      var i;
      for (i = 0; i < 5; i++) chips += '<span class="search-skeleton-chip"></span>';
      var cards = '';
      for (i = 0; i < 4; i++) {
        cards += '<div class="search-skeleton"><div class="search-skeleton__img"></div>' +
          '<div class="search-skeleton__line"></div><div class="search-skeleton__line"></div></div>';
      }
      return '<div class="search-results__layout" aria-hidden="true">' +
        '<div class="search-results__rail"><div class="search-skeleton-chips">' + chips + '</div></div>' +
        '<div class="search-results__main"><div class="search-grid search-grid--results">' + cards + '</div></div>' +
        '</div>';
    }

    function itemId() {
      itemSeq += 1;
      return 'SearchOption-' + itemSeq;
    }

    // One builder for both the discovery panel (asOption false — the cards live
    // outside the listbox) and the result grid (asOption true).
    function searchCard(raw, terms, source, asOption) {
      var item = normalizeProduct(raw, source);
      if (!item) return '';
      var media = item.image
        ? '<img class="search-card__img" src="' + esc(sizedImage(item.image, 480)) +
          '" alt="" width="240" height="240" loading="lazy">'
        : '<span class="search-card__img search-card__img--empty" aria-hidden="true"></span>';
      var badges = item.available ? ''
        : '<span class="search-card__badges"><span class="badge badge--soldout">' +
          esc(strings.soldOut || 'Stoc epuizat') + '</span></span>';
      var vendor = item.vendor
        ? '<span class="search-card__vendor">' + esc(item.vendor) + '</span>' : '';
      var price = item.price == null ? ''
        : '<span class="search-card__price">' + esc(formatMoney(item.price)) + '</span>';
      var compare = item.compare
        ? '<s class="search-card__compare">' + esc(formatMoney(item.compare)) + '</s>' : '';

      // The visible title keeps its <mark> highlights; the option's accessible
      // name carries price and availability so screen readers get the same
      // information sighted users read off the card.
      var label = item.title +
        (item.price == null ? '' : ', ' + formatMoney(item.price)) +
        (item.available ? '' : ', ' + (strings.soldOut || 'Stoc epuizat'));
      var optionAttrs = asOption
        ? ' data-search-item role="option" aria-selected="false" id="' + itemId() + '"'
        : '';

      // The title text is line-clamped with -webkit-box, which swallows
      // pseudo-elements, so the anchor that carries the card-wide hit area is a
      // separate element around it.
      return '<div class="search-card" data-product-card data-handle="' + esc(item.handle) + '">' +
        '<div class="search-card__media">' + media + badges + quickAddHTML(templates, item) + '</div>' +
        '<div class="search-card__body">' + vendor +
          '<a class="search-card__link" href="' + esc(item.url) + '"' + optionAttrs +
            ' aria-label="' + esc(label) + '">' +
            '<span class="search-card__title">' + highlight(item.title, terms) + '</span>' +
          '</a>' +
          '<span class="search-card__prices">' + price + compare + '</span>' +
        '</div>' +
      '</div>';
    }

    function chip(item) {
      return '<a class="search-chip" href="' + esc(item.url) + '" data-search-item role="option" aria-selected="false" id="' + itemId() + '">' +
        esc(item.title || item.text || '') + '</a>';
    }

    // Structural wrappers are presentational so the listbox still owns its
    // groups and options directly in the accessibility tree; the visible eyebrow
    // is hidden from AT because the group already carries the same label.
    function zone(labelText, inner, cls, count) {
      var chipCount = count ? '<span class="count">' + esc(String(count)) + '</span>' : '';
      return '<section class="search-zone" role="presentation">' +
        '<p class="search-label" aria-hidden="true">' + esc(labelText) + chipCount + '</p>' +
        '<div class="' + cls + '" role="group" aria-label="' + esc(labelText) + '">' + inner + '</div>' +
        '</section>';
    }

    // The "view all" anchor also contains an inline icon; setting textContent on
    // the anchor itself would delete it, so only the label text is replaced.
    function setViewAllLabel(label) {
      if (!viewAll) return;
      var labelEl = viewAll.querySelector('[data-search-viewall-label]');
      if (labelEl) { labelEl.textContent = label; return; }
      var replaced = false;
      Array.prototype.slice.call(viewAll.childNodes).forEach(function (node) {
        if (node.nodeType !== 3) return;
        if (replaced) { node.nodeValue = ''; return; }
        node.nodeValue = label;
        replaced = true;
      });
      if (!replaced) viewAll.insertBefore(document.createTextNode(label), viewAll.firstChild);
    }

    function interpolate(template, token, value) {
      return String(template || '').replace(token, function () { return String(value); });
    }

    function render(data, terms) {
      var res = (data && data.resources && data.resources.results) || {};
      var products = res.products || [];
      var queries = res.queries || [];
      var collections = res.collections || [];
      var pages = res.pages || [];
      // `total` stays the real result count used in the announcement; the
      // empty state is decided on the built markup so that query suggestions
      // are still shown when they are the only payload.
      var total = products.length + collections.length + pages.length;
      // Products come back capped at resources[limit]; hitting the cap is the
      // only signal the API gives that the full result set is larger.
      var truncated = products.length >= SEARCH_LIMIT;

      var rail = '';
      if (queries.length) {
        rail += zone(strings.searchSuggestions || 'Sugestii', queries.map(chip).join(''), 'search-chips');
      }
      if (collections.length) {
        rail += zone(strings.searchCollections || 'Colecții', collections.map(chip).join(''), 'search-chips', collections.length);
      }
      if (pages.length) {
        rail += zone(strings.searchPages || 'Pagini', pages.map(chip).join(''), 'search-chips', pages.length);
      }

      var main = '';
      if (products.length) {
        main = zone(
          strings.searchProducts || 'Produse',
          products.map(function (p) { return searchCard(p, terms, 'suggest', true); }).join(''),
          'search-grid search-grid--results',
          products.length
        );
      }

      var html;
      if (rail || main) {
        // Suggestions before products in DOM order: that is the reading order on
        // mobile (single column) and the arrow-key order on every viewport.
        html = '<div class="search-results__layout" role="presentation">' +
          (rail ? '<div class="search-results__rail" role="presentation">' + rail + '</div>' : '') +
          (main ? '<div class="search-results__main" role="presentation">' + main + '</div>' : '') +
          '</div>';
      } else {
        html = '<div class="search-empty">' +
          '<p class="search-empty__title">' + esc(interpolate(strings.searchNoResults || '', '[terms]', terms)) + '</p>' +
          '<p>' + esc(strings.searchNoResultsHint || '') + '</p></div>';
      }

      resultsBox.innerHTML = html;
      resultsBox.hidden = false;
      if (initialBox) initialBox.hidden = true;
      if (input) {
        input.setAttribute('aria-expanded', 'true');
        input.removeAttribute('aria-activedescendant');
      }

      // Left of the footer bar: which query the results belong to. Right of it:
      // how many there are. Past the cap the exact number is unknown, so the
      // count is marked as a floor rather than quoted as the real total.
      if (countEl) {
        countEl.textContent = interpolate(strings.searchResultsFor || 'Rezultate pentru „[terms]”', '[terms]', terms);
      }
      if (viewAll) {
        setViewAllLabel(interpolate(
          truncated
            ? (strings.searchViewAllMore || 'Vezi toate cele [count]+ rezultate')
            : (strings.searchViewAllCount || 'Vezi toate cele [count] rezultate'),
          '[count]', total
        ));
        viewAll.href = (routes.search || '/search') + '?q=' + encodeURIComponent(terms);
        viewAll.classList.add('is-visible');
        viewAll.setAttribute('data-search-item', '');
      }
      if (footer) footer.hidden = false;
      announce(interpolate(strings.searchResultsCount || '', '[count]', total));
      activeIndex = -1;
      initProductForms();
    }

    function doSearch(terms) {
      if (abortCtrl) abortCtrl.abort();
      abortCtrl = new AbortController();
      var ctrl = abortCtrl;

      resultsBox.innerHTML = skeletons();
      resultsBox.hidden = false;
      if (initialBox) initialBox.hidden = true;
      if (footer) footer.hidden = true;
      if (input) input.setAttribute('aria-expanded', 'true');
      announce(strings.searchSearching || '');

      var suggestRoute = routes.predictiveSearch || '/search/suggest';
      var url = suggestRoute + '.json?q=' + encodeURIComponent(terms) +
        '&resources[type]=product,query,collection,page' +
        '&resources[limit]=' + SEARCH_LIMIT +
        // resources[limit_scope] is a scalar ("each" or "all"). The nested form
        // resources[limit_scope][each] is rejected by the API with HTTP 422.
        '&resources[limit_scope]=each' +
        '&resources[options][unavailable_products]=last';

      fetchJSON(url, { signal: ctrl.signal })
        .then(function (data) {
          // Guard against out-of-order resolution and against a response that
          // lands after the field was cleared.
          if (ctrl !== abortCtrl) return;
          render(data, terms);
        })
        .catch(function (err) {
          if (err.name === 'AbortError' || ctrl !== abortCtrl) return;
          resultsBox.innerHTML = '<div class="search-empty"><p>' + esc(strings.searchError || '') + '</p></div>';
          if (footer) footer.hidden = true;
        });
    }

    // Shared entry point for pills, recent chips and programmatic queries.
    function runQuery(term) {
      var clean = String(term || '').trim();
      if (!input || !clean) return;
      input.value = clean;
      if (clearBtn) clearBtn.hidden = false;
      saveRecent(clean);
      doSearch(clean);
      input.focus();
    }

    function optionList() {
      var list = Array.prototype.slice.call(resultsBox.querySelectorAll('[data-search-item]'));
      if (viewAll && viewAll.classList.contains('is-visible')) list.push(viewAll);
      return list;
    }

    function clearActive() {
      if (activeIndex === -1) return;
      optionList().forEach(function (el) {
        el.classList.remove('is-active');
        if (el.getAttribute('role') === 'option') el.setAttribute('aria-selected', 'false');
        var card = el.closest ? el.closest('.search-card') : null;
        if (card) card.classList.remove('is-active');
      });
      activeIndex = -1;
      if (input) input.removeAttribute('aria-activedescendant');
    }

    function moveActive(dir) {
      var list = optionList();
      if (!list.length) return;
      activeIndex = (activeIndex + dir + list.length) % list.length;
      list.forEach(function (el, i) {
        var on = i === activeIndex;
        el.classList.toggle('is-active', on);
        if (el.getAttribute('role') === 'option') el.setAttribute('aria-selected', String(on));
        // The option is the title anchor; the highlight belongs to the whole
        // card, which is its own element.
        var card = el.closest ? el.closest('.search-card') : null;
        if (card) card.classList.toggle('is-active', on);
      });
      var active = list[activeIndex];
      active.scrollIntoView({ block: 'nearest' });
      if (input) {
        // Only options that live inside the listbox may be referenced.
        if (active.id && resultsBox.contains(active)) input.setAttribute('aria-activedescendant', active.id);
        else input.removeAttribute('aria-activedescendant');
      }
    }

    if (input) {
      input.addEventListener('input', function () {
        var terms = input.value.trim();
        if (clearBtn) clearBtn.hidden = terms.length === 0;
        clearTimeout(debounceTimer);
        if (terms.length < 2) { showInitial(); return; }
        debounceTimer = setTimeout(function () { doSearch(terms); }, 280);
      });

      input.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowDown') { e.preventDefault(); moveActive(1); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); moveActive(-1); }
        else if (e.key === 'Enter') {
          var active = modal.querySelector('[data-search-item].is-active');
          if (active) {
            e.preventDefault();
            saveRecent(input.value);
            window.location.href = active.href;
          } else if (input.value.trim()) {
            // No virtual selection: plain Enter runs the full search page.
            e.preventDefault();
            var terms = input.value.trim();
            saveRecent(terms);
            window.location.href = (routes.search || '/search') + '?q=' + encodeURIComponent(terms);
          }
        }
      });
    }

    if (form) {
      form.addEventListener('submit', function () {
        if (input) saveRecent(input.value);
      });
    }

    resultsBox.addEventListener('click', function (e) {
      if (!e.target.closest('[data-search-item]')) return;
      if (input) saveRecent(input.value);
      api.close();
    });

    if (clearBtn) {
      clearBtn.addEventListener('click', function () {
        input.value = '';
        clearBtn.hidden = true;
        showInitial();
        input.focus();
      });
    }

    // merchant-editable pills: fill + search
    modal.querySelectorAll('[data-search-pill]').forEach(function (pill) {
      pill.addEventListener('click', function () {
        runQuery(pill.getAttribute('data-search-pill') || pill.textContent.trim());
      });
    });

    renderRecent();
    initBestsellers();
  }

  /* ---------- hero slider ---------- */

  function initHero() {
    document.querySelectorAll('[data-hero]').forEach(function (hero) {
      if (hero.getAttribute('data-hero-bound') === 'true') return;
      hero.setAttribute('data-hero-bound', 'true');
      var slides = hero.querySelectorAll('.hero-slide');
      if (!slides.length) return;
      var dotsWrap = hero.querySelector('[data-hero-dots]');
      var live = hero.querySelector('[data-hero-live]');
      var autoplay = isDesignMode ? 0 : parseInt(hero.getAttribute('data-autoplay') || '0', 10);
      var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      var i = 0;
      var timer = null;

      function updateSlideState(slide, active) {
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
        if ('inert' in slide) slide.inert = !active;
        slide.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach(function (el) {
          if (active) {
            if (el.getAttribute('data-hero-tabindex') !== null) {
              var previous = el.getAttribute('data-hero-tabindex');
              previous === '' ? el.removeAttribute('tabindex') : el.setAttribute('tabindex', previous);
              el.removeAttribute('data-hero-tabindex');
            }
          } else if (el.getAttribute('data-hero-tabindex') === null) {
            el.setAttribute('data-hero-tabindex', el.getAttribute('tabindex') || '');
            el.setAttribute('tabindex', '-1');
          }
        });
      }

      function go(n, announce) {
        i = (n + slides.length) % slides.length;
        slides.forEach(function (slide, index) {
          updateSlideState(slide, index === i);
        });
        if (dotsWrap) {
          var dots = dotsWrap.querySelectorAll('button');
          dots.forEach(function (d, di) {
            var active = di === i;
            d.classList.toggle('is-active', active);
            d.setAttribute('aria-current', active ? 'true' : 'false');
          });
        }
        if (announce && live) {
          live.textContent = (strings.slideStatus || 'Imaginea [current] din [total]')
            .replace('[current]', String(i + 1))
            .replace('[total]', String(slides.length));
        }
      }
      hero._csSelectSlide = function (slide) {
        var selectedIndex = Array.prototype.indexOf.call(slides, slide);
        if (selectedIndex >= 0) go(selectedIndex, false);
      };

      if (dotsWrap && slides.length > 1) {
        slides.forEach(function (_, di) {
          var b = document.createElement('button');
          b.type = 'button';
          b.className = 'hero__dot' + (di === 0 ? ' is-active' : '');
          b.setAttribute('aria-label', (strings.slideStatus || 'Imaginea [current] din [total]')
            .replace('[current]', String(di + 1))
            .replace('[total]', String(slides.length)));
          b.setAttribute('aria-current', di === 0 ? 'true' : 'false');
          b.addEventListener('click', function () { go(di, true); restart(); });
          dotsWrap.appendChild(b);
        });
      }

      var prev = hero.querySelector('[data-hero-prev]');
      var next = hero.querySelector('[data-hero-next]');
      if (prev) prev.addEventListener('click', function () { go(i - 1, true); restart(); });
      if (next) next.addEventListener('click', function () { go(i + 1, true); restart(); });

      function start() {
        // Four independent listeners call start(); without this guard the
        // handle is overwritten and the orphaned interval can never be cleared.
        if (timer) return;
        if (!reduceMotion && autoplay > 0 && slides.length > 1 && !document.hidden) {
          timer = setInterval(function () { go(i + 1, false); }, autoplay);
          hero._csTimer = timer;
        }
      }
      function stop() {
        if (timer) clearInterval(timer);
        timer = null;
        hero._csTimer = null;
      }
      function restart() { stop(); start(); }

      hero.addEventListener('mouseenter', stop);
      hero.addEventListener('mouseleave', start);
      hero.addEventListener('focusin', stop);
      hero.addEventListener('focusout', function (event) {
        if (!hero.contains(event.relatedTarget)) start();
      });
      // Kept on the element so shopify:section:unload can detach it.
      hero._csVisibility = function () {
        document.hidden ? stop() : start();
      };
      hero._csStop = stop;
      document.addEventListener('visibilitychange', hero._csVisibility);

      slides.forEach(function (slide, index) {
        updateSlideState(slide, index === 0);
      });
      start();
    });
  }

  /* ---------- cart drawer ---------- */

  var CartDrawer = {
    el: null,
    api: null,
    cart: null,
    lastFocused: null,
    init: function () {
      this.el = document.getElementById('CartDrawer');
      if (!this.el) return;
      var self = this;

      this.api = {
        close: function () {
          if (!self.el) return;
          self.el.classList.remove('is-open');
          setDialogState(self.el, false);
          Overlay.hide();
          if (self.lastFocused) self.lastFocused.focus();
        }
      };

      // When the drawer is switched off in theme settings the header cart link
      // must navigate to /cart instead of opening a half-disabled slide-out.
      if (T.cartDrawer) {
        document.querySelectorAll('[data-open-cart]').forEach(function (b) {
          if (b.getAttribute('data-cart-opener-bound') === 'true') return;
          b.setAttribute('data-cart-opener-bound', 'true');
          b.addEventListener('click', function (e) {
            e.preventDefault();
            self.lastFocused = document.activeElement;
            self.open();
          });
        });
      }
      if (this.el.getAttribute('data-cart-drawer-bound') !== 'true') {
        this.el.setAttribute('data-cart-drawer-bound', 'true');
        this.el.querySelectorAll('[data-cart-close]').forEach(function (b) {
          b.addEventListener('click', self.api.close);
        });
        this.el.addEventListener('keydown', function (e) {
          if (e.key === 'Escape') self.api.close();
          if (e.key === 'Tab') trapFocus(self.el, e);
        });

        // qty / remove (delegated)
        this.el.addEventListener('click', function (e) {
          var qtyBtn = e.target.closest('[data-cart-qty]');
          var removeBtn = e.target.closest('[data-cart-remove]');
          if (qtyBtn) {
            self.change(qtyBtn.getAttribute('data-key'), parseInt(qtyBtn.getAttribute('data-qty'), 10));
          } else if (removeBtn) {
            self.change(removeBtn.getAttribute('data-key'), 0);
          }
        });

        // Checkout intent, consumed by the tracking layer before navigation.
        this.el.addEventListener('click', function (e) {
          var checkout = e.target.closest(
            '[data-cart-checkout], button[name="checkout"], a[href*="/checkout"]'
          );
          if (!checkout || !self.el.contains(checkout)) return;
          var cart = self.cart || {};
          // Contract (see the listener in layout/theme.liquid):
          // { items: [{ product_id, quantity }], total: subunits }
          dispatchThemeEvent('cs:checkout-intent', {
            items: (cart.items || []).map(function (item) {
              return { product_id: item.product_id, variant_id: item.variant_id, quantity: item.quantity || 1 };
            }),
            total: cart.total_price || 0
          });
        });
      }

      // Shopify's editor can misclassify background JSON requests as the
      // preview's current URL. The drawer already has server-rendered empty /
      // subtotal state, so defer this request until a storefront interaction.
      if (!isDesignMode && T.cartDrawer) {
        this.refresh(false).catch(function () { /* native cart link remains available */ });
      }
    },

    open: function () {
      if (!this.el) return;
      if (document.activeElement && !this.el.contains(document.activeElement)) {
        this.lastFocused = document.activeElement;
      }
      setDialogState(this.el, true);
      this.el.classList.add('is-open');
      Overlay.show(this.api);
      var closeButton = this.el.querySelector('[data-cart-close]');
      if (closeButton) closeButton.focus();
    },

    itemHTML: function (item) {
      var img = item.image
        ? '<img src="' + esc(sizedImage(item.image, 192)) + '" alt="" width="96" height="96" loading="lazy">'
        : '';
      var title = item.product_title || '';
      var decreaseLabel = (strings.cartDecrease || 'Scade cantitatea pentru [product]').replace('[product]', title);
      var increaseLabel = (strings.cartIncrease || 'Mărește cantitatea pentru [product]').replace('[product]', title);
      var removeLabel = (strings.cartRemove || 'Elimină');
      var variant = item.variant_title && item.variant_title !== 'Default Title'
        ? '<span class="cart-item__variant">' + esc(item.variant_title) + '</span>' : '';
      var props = '';
      if (item.properties) {
        Object.keys(item.properties).forEach(function (k) {
          if (item.properties[k] && k.charAt(0) !== '_') {
            props += '<span class="cart-item__variant">' + esc(k) + ': ' + esc(item.properties[k]) + '</span>';
          }
        });
      }
      // Mirror the server-rendered discount markup in sections/cart-drawer.liquid
      // so an AJAX refresh does not silently drop line-level discounts.
      var discounts = '';
      var allocations = item.line_level_discount_allocations || [];
      if (allocations.length) {
        discounts = '<ul class="cart-item__discounts" role="list">' + allocations.map(function (allocation) {
          var appliedTitle = (allocation.discount_application && allocation.discount_application.title) || allocation.title || '';
          return '<li class="cart-item__discount">' + esc(appliedTitle) + ' (−' + formatMoney(allocation.amount) + ')</li>';
        }).join('') + '</ul>';
      }
      var priceCell = item.original_line_price > item.final_line_price
        ? '<s class="price__compare">' + formatMoney(item.original_line_price) + '</s>' + formatMoney(item.final_line_price)
        : formatMoney(item.final_line_price);
      // Per-unit price, mirroring the `cart-item__unit` line in
      // sections/cart-drawer.liquid. `final_price` is per unit after line
      // discounts; the cell on the right is the line total.
      var unitText = (strings.cartUnitPrice || '[amount] / buc').replace('[amount]', function () {
        return formatMoney(item.final_price);
      });
      var unit = '<span class="cart-item__unit">' + esc(unitText) + '</span>';
      var totalLabel = strings.cartLineTotal || 'Total';

      return '<div class="cart-item" data-key="' + esc(item.key) + '">' +
        '<a class="cart-item__img" href="' + esc(item.url) + '">' + img + '</a>' +
        '<div class="cart-item__body">' +
          '<a class="cart-item__title" href="' + esc(item.url) + '">' + esc(item.product_title) + '</a>' +
          variant + props + unit + discounts +
          '<div class="cart-item__row">' +
            '<div class="qty">' +
              '<button type="button" data-cart-qty data-key="' + esc(item.key) + '" data-qty="' + (item.quantity - 1) + '" aria-label="' + esc(decreaseLabel) + '">−</button>' +
              '<span>' + item.quantity + '</span>' +
              '<button type="button" data-cart-qty data-key="' + esc(item.key) + '" data-qty="' + (item.quantity + 1) + '" aria-label="' + esc(increaseLabel) + '">+</button>' +
            '</div>' +
            '<span class="cart-item__price">' +
              '<span class="cart-item__price-label">' + esc(totalLabel) + '</span>' +
              '<span class="cart-item__price-amount">' + priceCell + '</span>' +
            '</span>' +
          '</div>' +
        '</div>' +
        '<button type="button" class="cart-item__remove" data-cart-remove data-key="' + esc(item.key) + '" aria-label="' + esc(removeLabel) + '">×</button>' +
      '</div>';
    },

    render: function (cart) {
      this.cart = cart;
      if (!this.el) return;
      var itemsBox = this.el.querySelector('[data-cart-items]');
      var footer = this.el.querySelector('[data-cart-footer]');
      var empty = this.el.querySelector('[data-cart-empty]');

      if (itemsBox) {
        itemsBox.innerHTML = (cart.items || []).map(this.itemHTML, this).join('');
      }
      var isEmpty = !cart.item_count;
      if (footer) footer.hidden = isEmpty;
      if (empty) empty.hidden = !isEmpty;

      var subtotal = this.el.querySelector('[data-cart-subtotal]');
      if (subtotal) subtotal.textContent = formatMoney(cart.total_price);

      // Cart-level discounts are server-rendered by sections/cart-drawer.liquid;
      // rebuild them here so they cannot go stale after a quantity change.
      var discountBox = this.el.querySelector('[data-cart-discounts]');
      if (discountBox) {
        discountBox.innerHTML = (cart.cart_level_discount_applications || []).map(function (discount) {
          return '<div class="cart-drawer__discount"><span>' + esc(discount.title || '') + '</span>' +
            '<span>−' + formatMoney(discount.total_allocated_amount) + '</span></div>';
        }).join('');
      }

      document.querySelectorAll('[data-cart-count]').forEach(function (badge) {
        badge.textContent = cart.item_count;
        badge.hidden = !cart.item_count;
      });

      // free shipping progress
      var threshold = T.freeShippingThreshold || 0;
      var track = this.el.querySelector('[data-shipping-track]');
      if (track) {
        if (!threshold) { track.hidden = true; }
        else {
          track.hidden = isEmpty;
          var bar = track.querySelector('[data-shipping-progress]');
          var progressTrack = track.querySelector('[data-shipping-progress-track]');
          var msg = track.querySelector('[data-shipping-msg]');
          var pct = Math.min(100, Math.round((cart.total_price / threshold) * 100));
          if (bar) bar.style.width = pct + '%';
          if (progressTrack) progressTrack.setAttribute('aria-valuenow', String(pct));
          var unlocked = cart.total_price >= threshold;
          // Drives the truck → check icon swap and the success colours in CSS,
          // so the JS never has to build icon markup of its own.
          track.classList.toggle('is-complete', unlocked);
          if (msg) {
            if (unlocked) {
              msg.textContent = strings.freeShippingUnlocked || '';
            } else {
              // The remaining amount is emphasised, so this branch writes HTML.
              // The translated template is escaped first and the amount is
              // injected through a replacer function, which keeps `$` sequences
              // in a money format from being read as replacement patterns.
              var remaining = formatMoney(threshold - cart.total_price);
              msg.innerHTML = esc(strings.freeShippingAway || '').replace('[amount]', function () {
                return '<strong class="shipping-bar__amount">' + esc(remaining) + '</strong>';
              });
            }
            msg.classList.toggle('success', unlocked);
          }
        }
      }
    },

    refresh: function (openAfter) {
      var self = this;
      if (!this.el) return Promise.resolve();
      return fetchJSON((routes.cart || '/cart') + '.js').then(function (cart) {
        self.render(cart);
        if (openAfter) self.open();
      });
    },

    change: function (key, quantity) {
      var self = this;
      if (!self.el) return Promise.resolve();
      self.el.setAttribute('aria-busy', 'true');
      return fetchJSON((routes.cartChange || '/cart/change') + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: key, quantity: quantity })
      }).then(function (cart) {
        self.render(cart);
      }).catch(function () {
        window.location.href = routes.cart || '/cart';
      }).finally(function () {
        self.el.removeAttribute('aria-busy');
      });
    },

    // Rejects only when /cart/add.js itself failed. The follow-up refresh can
    // never reject, so a hiccup after a successful add can no longer make the
    // caller fall back to a native form POST and add the same line twice.
    add: function (formData) {
      var self = this;
      return fetch((routes.cartAdd || '/cart/add') + '.js', {
        method: 'POST',
        headers: { 'Accept': 'application/json' },
        body: formData
      })
        .then(function (r) {
          return r.json().catch(function () { return null; }).then(function (body) {
            if (!r.ok) {
              // Shopify answers 422 with {status, message, description} for
              // inventory limits and unavailable variants — surface that text.
              var error = new Error('add failed');
              error.cartError = true;
              error.description = (body && (body.description || body.message)) || '';
              throw error;
            }
            var line = (body && body.items && body.items[0]) || body || {};
            dispatchThemeEvent('cs:add-to-cart', {
              variantId: line.variant_id || line.id || null,
              price: typeof line.final_price === 'number' ? line.final_price : (line.price || null),
              quantity: line.quantity || 1
            });
            // Swallowed on purpose: the line is already in the cart, so a
            // failed refresh must never propagate into the caller's fallback.
            return self.refresh(true)
              .catch(function () { /* drawer keeps its current contents */ })
              .then(function () { return body; });
          });
        });
    }
  };

  /* ---------- product forms (AJAX add-to-cart) ---------- */

  // Shopify rejects add-to-cart with a localized description (inventory limit,
  // variant no longer available). Nothing in the templates renders it, so the
  // alert region is created next to the button that failed.
  function cartErrorScope(form) {
    return form.closest('[data-product-card]') || form;
  }

  function cartErrorBox(form) {
    var scope = cartErrorScope(form);
    var box = scope.querySelector('[data-cart-error]');
    if (box) return box;
    box = document.createElement('p');
    box.className = 'form-status form-status--error';
    box.setAttribute('role', 'alert');
    box.setAttribute('data-cart-error', '');
    if (scope !== form) {
      // Product card: the quick-add button is absolutely positioned over the
      // image, so the message belongs in the card body.
      (scope.querySelector('.product-card__info') || scope).appendChild(box);
    } else {
      var anchor = form.querySelector('[data-pdp-atc]') || form.querySelector('[type="submit"]');
      ((anchor && anchor.parentNode) || form).appendChild(box);
    }
    return box;
  }

  function showCartError(form, message) {
    var box = cartErrorBox(form);
    box.textContent = message;
    box.hidden = false;
  }

  function clearCartError(form) {
    var box = cartErrorScope(form).querySelector('[data-cart-error]');
    if (box) { box.textContent = ''; box.hidden = true; }
  }

  function cartError(description) {
    var error = new Error('cart add failed');
    error.cartError = true;
    error.description = description || '';
    return error;
  }

  // Quick-add cards built from /search/suggest.json know a product has exactly
  // one variant but not which one — that payload drops the variants array for
  // default-variant products. The id is filled in from the product JSON on the
  // first click, so the modal never has to pre-fetch 10 products.
  function resolveQuickAddVariant(form) {
    var idInput = form.querySelector('[name="id"]');
    if (!idInput) return Promise.resolve();
    if (idInput.value) return Promise.resolve();
    var card = form.closest('[data-handle]');
    var handle = form.getAttribute('data-quick-add-handle') ||
      (card ? card.getAttribute('data-handle') : '');
    // Without a handle there is nothing to look up, and a native POST with an
    // empty id would 404 — fail in place instead.
    if (!handle) return Promise.reject(cartError(strings.cartError || ''));

    return fetchJSON(rooted('products/' + encodeURIComponent(handle) + '.js'))
      .then(function (product) {
        var variants = (product && product.variants) || [];
        var chosen = null;
        for (var i = 0; i < variants.length; i++) {
          if (variants[i] && variants[i].available !== false) { chosen = variants[i]; break; }
        }
        if (!chosen || !chosen.id) throw cartError(strings.soldOut || '');
        idInput.value = chosen.id;
      })
      .catch(function (err) {
        // Any failure here must stay a cartError: the caller's fallback is a
        // native form POST, which would submit the still-empty id.
        throw (err && err.cartError) ? err : cartError(strings.cartError || '');
      });
  }

  function initProductForms() {
    if (!T.cartDrawer) return;
    document.querySelectorAll('form[data-product-form]').forEach(function (form) {
      if (form.getAttribute('data-product-form-bound') === 'true') return;
      form.setAttribute('data-product-form-bound', 'true');
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var btn = form.querySelector('[type="submit"]');
        // The lazy variant lookup makes this genuinely async, so a second click
        // while it is in flight would add the line twice.
        if (btn && btn.classList.contains('is-loading')) return;
        if (btn) btn.classList.add('is-loading');
        clearCartError(form);
        resolveQuickAddVariant(form).then(function () {
          return CartDrawer.add(new FormData(form));
        }).catch(function (err) {
          // A rejection from Shopify (sold out, inventory limit) is shown in
          // place; only a transport failure falls back to the native POST.
          if (err && err.cartError) {
            showCartError(form, err.description || strings.cartError || 'Nu am putut adăuga produsul în coș.');
            return;
          }
          form.submit(); // graceful fallback
        }).finally(function () {
          if (btn) btn.classList.remove('is-loading');
        });
      });
    });
  }

  function wishlistCountText(n) {
    return n === 1
      ? (strings.wishlistCountOne || 'produs salvat în acest browser')
      : (strings.wishlistCountOther || 'produse salvate în acest browser');
  }

  /* ---------- product page (PDP) ---------- */

  function initPDP() {
    var pdp = document.querySelector('[data-pdp]');
    if (!pdp) return;
    if (pdp.getAttribute('data-pdp-bound') === 'true') return;
    pdp.setAttribute('data-pdp-bound', 'true');

    // variant engine
    var variantsEl = document.getElementById('ProductVariants');
    if (variantsEl) {
      var variants = [];
      try { variants = JSON.parse(variantsEl.textContent); } catch (e) { return; }

      var idInput = pdp.querySelector('input[name="id"]');
      var priceBox = pdp.querySelector('[data-pdp-price]');
      var atcBtn = pdp.querySelector('[data-pdp-atc]');
      var stockEl = pdp.querySelector('[data-pdp-stock]');
      var skuEl = pdp.querySelector('[data-pdp-sku]');
      var mainImg = pdp.querySelector('[data-pdp-main]');
      var optionGroups = pdp.querySelectorAll('[data-option-position]');

      function selectedOptions() {
        var opts = [];
        optionGroups.forEach(function (group) {
          var active = group.querySelector('[data-option-value].is-selected');
          opts[parseInt(group.getAttribute('data-option-position'), 10) - 1] =
            active ? active.getAttribute('data-option-value') : null;
        });
        return opts;
      }

      // A product with only the default variant renders no option groups, so
      // opts is empty and every comparison against "Default Title" would fail.
      function findVariant(opts) {
        if (!opts || !opts.length) return null;
        return variants.find(function (v) {
          return v.options.every(function (o, i) { return o === opts[i]; });
        }) || null;
      }

      function currentVariant() {
        var byId = idInput && variants.filter(function (v) {
          return String(v.id) === String(idInput.value);
        })[0];
        return byId || (variants.length === 1 ? variants[0] : null);
      }

      function setAtcState(available) {
        var label = available
          ? (strings.addToCart || 'Adaugă în coș')
          : (strings.soldOut || 'Stoc epuizat');
        if (atcBtn) {
          atcBtn.disabled = !available;
          var labelEl = atcBtn.querySelector('[data-atc-label]');
          if (labelEl) labelEl.textContent = label;
          else atcBtn.textContent = label;
        }
        // The sticky bar forwards its click to the submit button, so it must
        // never advertise a state the real button does not have.
        var stickyButton = document.querySelector('[data-sticky-atc] button');
        if (stickyButton) {
          stickyButton.disabled = !available;
          stickyButton.textContent = label;
        }
      }

      function update(variant, opts, updateUrl) {
        if (variant) {
          if (idInput) idInput.value = variant.id;
          if (priceBox) {
            var html = '<span class="price"><span class="price__current">' + formatMoney(variant.price) + '</span></span>';
            if (variant.compare_at_price > variant.price) {
              html = '<span class="price price--sale"><span class="price__current">' + formatMoney(variant.price) + '</span>' +
                '<s class="price__compare">' + formatMoney(variant.compare_at_price) + '</s></span>';
            }
            priceBox.innerHTML = html;
          }
          setAtcState(variant.available);
          if (stockEl) {
            var qty = variant.inventory_quantity;
            if (variant.available && qty > 0 && qty <= 5) {
              if (qty === 1) {
                stockEl.textContent = strings.lowStockOne || 'Ultimul produs disponibil!';
              } else if (qty <= 3) {
                stockEl.textContent = (strings.lowStockFew || 'Doar [count] bucăți rămase!').replace('[count]', qty);
              } else {
                stockEl.textContent = (strings.lowStockLimited || 'Stoc limitat — [count] bucăți disponibile').replace('[count]', qty);
              }
              stockEl.hidden = false;
            } else { stockEl.hidden = true; }
          }
          if (skuEl) skuEl.textContent = variant.sku || '—';
          if (updateUrl && !isDesignMode) {
            var url = new URL(window.location.href);
            url.searchParams.set('variant', variant.id);
            window.history.replaceState({}, '', url.toString());
          }
          if (variant.featured_media && mainImg) {
            mainImg.src = variant.featured_media;
            // Keep a responsive srcset and move the thumbnail highlight with
            // the hero image instead of blanking one and desyncing the other.
            var matchingThumb = null;
            pdp.querySelectorAll('[data-pdp-thumb]').forEach(function (thumb) {
              if (thumb.getAttribute('data-full') === variant.featured_media) matchingThumb = thumb;
            });
            if (matchingThumb && matchingThumb.getAttribute('data-srcset')) {
              mainImg.srcset = matchingThumb.getAttribute('data-srcset');
            } else {
              mainImg.removeAttribute('srcset');
            }
            pdp.querySelectorAll('[data-pdp-thumb]').forEach(function (thumb) {
              thumb.classList.toggle('is-active', thumb === matchingThumb);
            });
          }
          var stickyPrice = document.querySelector('[data-sticky-atc] .sticky-atc__price');
          if (stickyPrice) stickyPrice.textContent = formatMoney(variant.price);
        } else {
          setAtcState(false);
        }

        // availability state on pills
        optionGroups.forEach(function (group) {
          var pos = parseInt(group.getAttribute('data-option-position'), 10) - 1;
          group.querySelectorAll('[data-option-value]').forEach(function (pill) {
            var test = opts.slice();
            test[pos] = pill.getAttribute('data-option-value');
            var match = findVariant(test);
            pill.classList.toggle('is-unavailable', !match || !match.available);
          });
        });
      }

      optionGroups.forEach(function (group) {
        group.addEventListener('click', function (e) {
          var pill = e.target.closest('[data-option-value]');
          if (!pill) return;
          group.querySelectorAll('[data-option-value]').forEach(function (p) {
            p.classList.remove('is-selected');
            p.setAttribute('aria-pressed', 'false');
          });
          pill.classList.add('is-selected');
          pill.setAttribute('aria-pressed', 'true');
          var opts = selectedOptions();
          update(findVariant(opts), opts, true);
        });
      });
      // On boot never trust the option pills alone: a default-variant product
      // renders none, so fall back to the server-selected id (then to the only
      // variant) rather than disabling an available Add to cart button.
      var initialOpts = selectedOptions();
      update(findVariant(initialOpts) || currentVariant(), initialOpts, false);
    }

    // gallery thumbs
    var main = pdp.querySelector('[data-pdp-main]');
    pdp.querySelectorAll('[data-pdp-thumb]').forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        if (main) {
          main.src = thumb.getAttribute('data-full');
          if (thumb.getAttribute('data-srcset')) main.srcset = thumb.getAttribute('data-srcset');
        }
        pdp.querySelectorAll('[data-pdp-thumb]').forEach(function (t) { t.classList.remove('is-active'); });
        thumb.classList.add('is-active');
      });
    });

    // quantity controls
    var quantityInput = pdp.querySelector('input[name="quantity"]');
    pdp.querySelectorAll('[data-product-qty]').forEach(function (button) {
      button.addEventListener('click', function () {
        if (!quantityInput) return;
        if (button.getAttribute('data-product-qty') === 'increase') quantityInput.stepUp();
        else quantityInput.stepDown();
        quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });

    // Delivery estimate in Romanian business days, based on Bucharest time, plus
    // the live same-day cutoff countdown. One cutoff value feeds both, so the
    // countdown can never promise a ship day the estimate does not show.
    var deliveryBox = document.querySelector('[data-delivery]');
    if (deliveryBox) {
      var deliveryDate = deliveryBox.querySelector('[data-delivery-date]');
      var deliverySub = deliveryBox.querySelector('[data-delivery-sub]');
      var deliveryCutoff = deliveryBox.querySelector('[data-delivery-cutoff]');
      var cutoffHour = parseInt(deliveryBox.getAttribute('data-cutoff-hour'), 10);
      // 15:00 Romania time is the store's dispatch cutoff and the schema default;
      // anything the merchant cannot actually pick in the editor falls back to it.
      if (isNaN(cutoffHour) || cutoffHour < 0 || cutoffHour > 23) cutoffHour = 15;

      // Romanian duration grammar: "1 minut" / "8 minute" / "21 de minute",
      // "1 oră" / "2 ore" / "20 de ore", joined with "și".
      function roUnit(n, one, many) {
        return n + (n >= 20 ? ' de ' : ' ') + (n === 1 ? one : many);
      }
      function formatDurationRo(hours, minutes) {
        if (hours > 0 && minutes > 0) return roUnit(hours, 'oră', 'ore') + ' și ' + roUnit(minutes, 'minut', 'minute');
        if (hours > 0) return roUnit(hours, 'oră', 'ore');
        return roUnit(Math.max(minutes, 1), 'minut', 'minute');
      }

      function addWorkingDays(date, days) {
        var d = new Date(date);
        var added = 0;
        while (added < days) {
          d.setDate(d.getDate() + 1);
          if (d.getDay() !== 0 && d.getDay() !== 6) added += 1;
        }
        return d;
      }

      function formatDeliveryDate(date) {
        return new Intl.DateTimeFormat(T.locale || 'ro-RO', {
          weekday: 'long',
          day: 'numeric',
          month: 'long'
        }).format(date);
      }

      // Recomputed on every tick: the shopper may leave the tab open across the
      // cutoff, across midnight, or across a DST change.
      function romaniaNow() {
        var stamp = new Date();
        try {
          var parts = {};
          new Intl.DateTimeFormat('ro-RO', {
            timeZone: 'Europe/Bucharest',
            hour: 'numeric',
            minute: 'numeric',
            second: 'numeric',
            hour12: false,
            year: 'numeric',
            month: 'numeric',
            day: 'numeric'
          }).formatToParts(stamp).forEach(function (part) {
            parts[part.type] = parseInt(part.value, 10);
          });
          return new Date(
            parts.year,
            parts.month - 1,
            parts.day,
            parts.hour % 24,
            parts.minute,
            isNaN(parts.second) ? 0 : parts.second
          );
        } catch (e) {
          return stamp; // fall back to the browser time zone
        }
      }

      function sameDay(a, b) {
        return a.getFullYear() === b.getFullYear() &&
          a.getMonth() === b.getMonth() &&
          a.getDate() === b.getDate();
      }

      // `time` non-empty splits the template on [time] and wraps the remaining
      // hours/minutes in the accent <strong>; empty renders plain copy.
      function setCutoffLine(template, time) {
        if (!deliveryCutoff) return;
        var text = String(template || '');
        deliveryCutoff.textContent = '';
        if (time) {
          var pieces = text.split('[time]');
          deliveryCutoff.appendChild(document.createTextNode(pieces[0]));
          var strong = document.createElement('strong');
          strong.className = 'pdp__delivery__countdown';
          strong.textContent = time;
          deliveryCutoff.appendChild(strong);
          deliveryCutoff.appendChild(document.createTextNode(pieces.length > 1 ? pieces[1] : ''));
        } else {
          deliveryCutoff.textContent = text;
        }
        deliveryCutoff.hidden = false;
      }

      function renderDelivery() {
        var now = romaniaNow();
        var weekend = now.getDay() === 0 || now.getDay() === 6;
        var cutoffAt = new Date(now.getFullYear(), now.getMonth(), now.getDate(), cutoffHour, 0, 0);
        var remaining = cutoffAt.getTime() - now.getTime();
        var shipsToday = !weekend && remaining > 0;
        var shipDay = shipsToday ? new Date(now) : addWorkingDays(now, 1);

        var minimum = formatDeliveryDate(addWorkingDays(shipDay, 1));
        var maximum = formatDeliveryDate(addWorkingDays(shipDay, 2));
        var range = minimum === maximum ? minimum : minimum + ' – ' + maximum;

        if (deliveryDate) deliveryDate.textContent = range;
        deliveryBox.setAttribute(
          'aria-label',
          (strings.deliveryAriaLabel || 'Livrare estimată: [date]').replace('[date]', range)
        );

        if (shipsToday) {
          var minutes = Math.ceil(remaining / 60000);
          var hours = Math.floor(minutes / 60);
          minutes = minutes % 60;
          setCutoffLine(
            strings.deliveryCountdown || 'Comandă în următoarele [time] și expediem azi',
            formatDurationRo(hours, minutes)
          );
          return;
        }

        // Weekends always get the "first working day" copy even when the ship
        // day is literally tomorrow (Sunday); a Friday after cutoff gets it too,
        // because the next dispatch is Monday, not tomorrow morning.
        var tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
        if (!weekend && sameDay(shipDay, tomorrow)) {
          setCutoffLine(strings.deliveryShipsTomorrow || 'Expediem mâine dimineață ✓', '');
        } else {
          setCutoffLine(strings.deliveryShipsNextWorkday || 'Expediem în prima zi lucrătoare ✓', '');
        }
      }

      if (deliverySub) deliverySub.textContent = strings.deliverySub || '';
      renderDelivery();
      // Editor previews get the resolved state but no timer, matching the
      // announcement bar; the handle is kept on the element so
      // shopify:section:unload can clear it.
      if (!isDesignMode) {
        deliveryBox._csTimer = window.setInterval(renderDelivery, 60000);
      }
    }

    // sticky ATC
    var form = pdp.querySelector('form[data-product-form]');
    var sticky = document.querySelector('[data-sticky-atc]');
    if (form && sticky && 'IntersectionObserver' in window) {
      var observer = new IntersectionObserver(function (entries) {
        sticky.classList.toggle('is-visible', !entries[0].isIntersecting && entries[0].boundingClientRect.top < 0);
      }, { threshold: 0 });
      observer.observe(form);
      var stickyBtn = sticky.querySelector('button');
      if (stickyBtn) {
        stickyBtn.addEventListener('click', function () {
          var submit = form.querySelector('[type="submit"]');
          if (submit) submit.click();
        });
      }
    }

    // recently viewed — store
    var handle = pdp.getAttribute('data-handle');
    if (handle) {
      try {
        var stored = JSON.parse(localStorage.getItem('cs-recently-viewed') || '[]');
        stored = stored.filter(function (h) { return h !== handle; });
        stored.unshift(handle);
        localStorage.setItem('cs-recently-viewed', JSON.stringify(stored.slice(0, 12)));
      } catch (e) { /* storage unavailable */ }
    }
  }

  /* ---------- recently viewed render ---------- */

  // 30 days, matching the `age < 2592000` window in snippets/product-card.liquid.
  var RECENT_NEW_WINDOW_MS = 2592000 * 1000;

  function initRecentlyViewed() {
    var section = document.querySelector('[data-recently-viewed]');
    if (!section) return;
    if (isDesignMode) {
      // The grid can only be filled from localStorage + product JSON, both of
      // which are unavailable in the editor. Showing a bare heading over an
      // empty grid is worse than leaving the section hidden.
      section.setAttribute('data-editor-network-paused', 'true');
      return;
    }
    if (section.getAttribute('data-recently-viewed-bound') === 'true') return;
    section.setAttribute('data-recently-viewed-bound', 'true');
    var grid = section.querySelector('[data-rv-grid]');
    var tpl = section.querySelector('[data-rv-template]');
    if (!grid || !tpl) return;
    var templates = readCardTemplates(section);

    var exclude = section.getAttribute('data-exclude') || '';
    var handles = [];
    try {
      handles = JSON.parse(localStorage.getItem('cs-recently-viewed') || '[]')
        .filter(function (h) { return h && h !== exclude; })
        .slice(0, 8);
    } catch (e) { return; }
    if (!handles.length) return;

    // Resolved together and appended in order, so the grid keeps the recency
    // order stored in localStorage instead of the order responses arrive in.
    Promise.all(handles.map(function (handle) {
      return fetchJSON(rooted('products/' + encodeURIComponent(handle) + '.js'))
        .catch(function () { return null; });
    })).then(function (products) {
      var valid = products.filter(Boolean);
      if (!valid.length) return;
      valid.forEach(function (raw) {
        var item = normalizeProduct(raw, 'product');
        if (!item) return;
        var node = tpl.content.cloneNode(true);
        var card = node.querySelector('.product-card');
        var media = node.querySelector('.product-card__media');
        var img = node.querySelector('[data-rv-img]');
        var hoverImg = node.querySelector('[data-rv-img-hover]');
        var badges = node.querySelector('[data-rv-badges]');
        var vendor = node.querySelector('[data-rv-vendor]');
        var title = node.querySelector('[data-rv-title]');
        var price = node.querySelector('[data-rv-price]');
        var compare = node.querySelector('[data-rv-compare]');
        var rating = node.querySelector('[data-rv-rating]');
        var toggle = node.querySelector('[data-wishlist-toggle]');

        if (card) card.setAttribute('data-handle', item.handle);
        node.querySelectorAll('[data-rv-link]').forEach(function (a) {
          a.href = item.url;
          if (a.hasAttribute('aria-label')) a.setAttribute('aria-label', item.title);
        });

        if (img && item.image) {
          img.src = sizedImage(item.image, 600);
          img.alt = item.title;
        } else if (img) {
          // Same branded placeholder snippets/product-card.liquid falls back to.
          img.remove();
          var link = node.querySelector('[data-rv-link]');
          if (link && templates.mediaFallback) link.innerHTML = templates.mediaFallback;
        }
        // Mirrors snippets/product-card.liquid: the hover layer only exists when
        // the product actually has a second image.
        if (hoverImg) {
          if (item.image && item.hoverImage) hoverImg.src = sizedImage(item.hoverImage, 600);
          else hoverImg.remove();
        }

        // Same precedence as snippets/product-card.liquid: sold out wins, then
        // the discount badge, then the 30-day "new" badge.
        if (badges) {
          if (!item.available && templates.soldOut) badges.innerHTML = templates.soldOut;
          else if (item.compare && templates.sale) {
            var pct = Math.round((item.compare - item.price) * 100 / item.compare);
            badges.innerHTML = fillTokens(templates.sale, { '__PCT__': pct });
          } else if (templates.isNew && item.publishedAt) {
            var published = Date.parse(item.publishedAt);
            if (!isNaN(published) && (Date.now() - published) < RECENT_NEW_WINDOW_MS) {
              badges.innerHTML = templates.isNew;
            }
          }
        }

        if (vendor && item.vendor) {
          vendor.textContent = item.vendor;
          vendor.hidden = false;
        }
        if (title) title.textContent = item.title;
        // Judge.me placeholder — the app's script (loaded via its app embed)
        // swaps it for the star rating on customizeBadges(). Dropped entirely
        // when Judge.me is absent or the id is unknown, so no empty row is left.
        if (rating) {
          if (item.id && window.jdgm) {
            var jdgmBadge = document.createElement('span');
            jdgmBadge.className = 'jdgm-prev-badge';
            jdgmBadge.setAttribute('data-id', item.id);
            rating.appendChild(jdgmBadge);
            rating.hidden = false;
          } else {
            rating.remove();
          }
        }
        if (price && item.price != null) price.textContent = formatMoney(item.price);
        if (compare && item.compare) {
          compare.textContent = formatMoney(item.compare);
          compare.hidden = false;
        }

        if (media) {
          var quickAdd = quickAddHTML(templates, item);
          if (quickAdd) media.insertAdjacentHTML('beforeend', quickAdd);
        }

        if (toggle) {
          toggle.setAttribute('data-handle', item.handle);
          toggle.setAttribute('data-wishlist-add-label', (strings.wishlistAdd || '').replace('[product]', item.title));
          toggle.setAttribute('data-wishlist-remove-label', (strings.wishlistRemove || '').replace('[product]', item.title));
        }
        grid.appendChild(node);
      });
      section.hidden = false;
      Wishlist.syncButtons();
      initProductForms();
      // Judge.me only scans for badges on its own load; cards appended after
      // that need an explicit re-scan (same 100ms handoff the production theme
      // used, which gives the app time to finish its own first pass).
      if (window.jdgm && typeof window.jdgm.customizeBadges === 'function') {
        setTimeout(function () {
          try { window.jdgm.customizeBadges(); } catch (e) {}
        }, 100);
      }
    });
  }

  /* ---------- wishlist (localStorage) ---------- */

  var Wishlist = {
    KEY: 'cs-wishlist',
    drawer: null,
    api: null,
    lastFocused: null,
    read: function () {
      try { return JSON.parse(localStorage.getItem(this.KEY) || '[]'); }
      catch (e) { return []; }
    },
    write: function (list) {
      try { localStorage.setItem(this.KEY, JSON.stringify(list)); } catch (e) {}
    },
    has: function (handle) { return this.read().indexOf(handle) !== -1; },
    toggle: function (handle) {
      var list = this.read();
      var idx = list.indexOf(handle);
      if (idx === -1) list.push(handle); else list.splice(idx, 1);
      this.write(list);
      return idx === -1;
    },
    syncBadges: function () {
      var count = this.read().length;
      document.querySelectorAll('[data-wishlist-count]').forEach(function (b) {
        b.textContent = count;
        b.hidden = !count;
      });
    },
    syncButtons: function () {
      var self = this;
      document.querySelectorAll('[data-wishlist-toggle]').forEach(function (btn) {
        var on = self.has(btn.getAttribute('data-handle'));
        btn.setAttribute('aria-pressed', String(on));
        btn.classList.toggle('is-active', on);
        var label = on
          ? btn.getAttribute('data-wishlist-remove-label')
          : btn.getAttribute('data-wishlist-add-label');
        if (label) btn.setAttribute('aria-label', label);
      });
    },
    drawerItemHTML: function (p) {
      var image = p.featured_image
        ? '<img src="' + esc(sizedImage(p.featured_image, 240)) + '" alt="" width="120" height="120" loading="lazy">'
        : '';
      return '<article class="wishlist-drawer__item" data-handle="' + esc(p.handle) + '">' +
        '<a class="wishlist-drawer__image" href="' + esc(p.url) + '">' + image + '</a>' +
        '<div class="wishlist-drawer__body">' +
          '<a class="wishlist-drawer__title" href="' + esc(p.url) + '">' + esc(p.title) + '</a>' +
          '<span class="wishlist-drawer__price">' + formatMoney(p.price) + '</span>' +
        '</div>' +
        '<button type="button" class="wishlist-drawer__remove" data-wishlist-drawer-remove="' + esc(p.handle) + '" aria-label="' +
          esc((strings.wishlistRemove || 'Elimină [product] din favorite').replace('[product]', p.title)) + '">×</button>' +
      '</article>';
    },
    renderDrawer: function () {
      if (!this.drawer) return Promise.resolve();
      var self = this;
      var items = this.drawer.querySelector('[data-wishlist-drawer-items]');
      var empty = this.drawer.querySelector('[data-wishlist-drawer-empty]');
      var handles = this.read();
      if (!items) return Promise.resolve();
      if (isDesignMode) {
        // Editor safe mode: never issue product lookups from the preview.
        this.drawer.setAttribute('data-editor-network-paused', 'true');
        if (empty) empty.hidden = false;
        return Promise.resolve();
      }
      if (!handles.length) {
        items.innerHTML = '';
        if (empty) empty.hidden = false;
        return Promise.resolve();
      }
      if (empty) empty.hidden = true;
      items.innerHTML = '<div class="search-skeleton" aria-label="' +
        esc(strings.wishlistLoading || 'Se încarcă favoritele') + '"></div>';
      return Promise.all(handles.map(function (handle) {
        return fetchJSON(rooted('products/' + encodeURIComponent(handle) + '.js'))
          .catch(function () { return null; });
      })).then(function (products) {
        var valid = products.filter(Boolean);
        items.innerHTML = valid.map(function (p) { return self.drawerItemHTML(p); }).join('');
        var validHandles = valid.map(function (p) { return p.handle; });
        if (validHandles.length !== handles.length) {
          self.write(validHandles);
          self.syncBadges();
          self.syncButtons();
        }
        if (empty) empty.hidden = valid.length > 0;
      });
    },
    open: function (opener) {
      if (!this.drawer) return;
      this.lastFocused = opener || document.activeElement;
      setDialogState(this.drawer, true);
      this.drawer.classList.add('is-open');
      Overlay.show(this.api);
      this.renderDrawer();
      var closeButton = this.drawer.querySelector('[data-wishlist-close]');
      if (closeButton) closeButton.focus();
    },
    close: function () {
      if (!this.drawer) return;
      this.drawer.classList.remove('is-open');
      setDialogState(this.drawer, false);
      Overlay.hide();
      if (
        this.lastFocused &&
        document.contains(this.lastFocused) &&
        !this.lastFocused.closest('[inert]') &&
        this.lastFocused.offsetParent !== null
      ) this.lastFocused.focus();
    },
    // The previous theme stored product IDs under `cs_wishlist_ids`. Those IDs
    // cannot be resolved to handles from the storefront, so the list cannot be
    // migrated — just drop the key instead of leaving it in localStorage.
    cleanupLegacy: function () {
      try {
        if (localStorage.getItem('cs_wishlist_ids') !== null) {
          localStorage.removeItem('cs_wishlist_ids');
        }
      } catch (e) { /* storage unavailable */ }
    },
    init: function () {
      var self = this;
      this.cleanupLegacy();
      this.drawer = document.getElementById('WishlistDrawer');
      this.api = { close: function () { self.close(); } };
      document.querySelectorAll('[data-open-wishlist]').forEach(function (opener) {
        if (opener.getAttribute('data-wishlist-opener-bound') === 'true') return;
        opener.setAttribute('data-wishlist-opener-bound', 'true');
        opener.addEventListener('click', function () { self.open(opener); });
      });
      if (this.drawer && this.drawer.getAttribute('data-wishlist-drawer-bound') !== 'true') {
        this.drawer.setAttribute('data-wishlist-drawer-bound', 'true');
        this.drawer.querySelectorAll('[data-wishlist-close]').forEach(function (closer) {
          closer.addEventListener('click', function () { self.close(); });
        });
        this.drawer.addEventListener('keydown', function (e) {
          if (e.key === 'Escape') self.close();
          if (e.key === 'Tab') trapFocus(self.drawer, e);
        });
        this.drawer.addEventListener('click', function (e) {
          var remove = e.target.closest('[data-wishlist-drawer-remove]');
          if (!remove) return;
          var handle = remove.getAttribute('data-wishlist-drawer-remove');
          self.write(self.read().filter(function (item) { return item !== handle; }));
          self.syncButtons();
          self.syncBadges();
          self.renderDrawer();
        });
      }
      document.querySelectorAll('[data-wishlist-clear]').forEach(function (clearButton) {
        if (clearButton.getAttribute('data-wishlist-clear-bound') === 'true') return;
        clearButton.setAttribute('data-wishlist-clear-bound', 'true');
        clearButton.addEventListener('click', function () {
          self.write([]);
          self.syncButtons();
          self.syncBadges();
          self.renderDrawer();
          self.renderPage();
        });
      });
      if (document.documentElement.getAttribute('data-wishlist-global-bound') !== 'true') {
        document.documentElement.setAttribute('data-wishlist-global-bound', 'true');
        document.addEventListener('click', function (e) {
          var btn = e.target.closest('[data-wishlist-toggle]');
          if (!btn) return;
          var handle = btn.getAttribute('data-handle');
          var added = self.toggle(handle);
          self.syncButtons();
          self.syncBadges();
          if (self.drawer && self.drawer.classList.contains('is-open')) self.renderDrawer();
          // on wishlist page: remove card instantly
          var page = document.querySelector('[data-wishlist-page]');
          if (page && !added) {
            var card = btn.closest('[data-wishlist-item]');
            if (card) card.remove();
            // Update the header locally — a full renderPage() would re-fetch
            // every remaining product just to change one number.
            var remaining = self.read().length;
            var pageCount = page.querySelector('[data-wishlist-page-count]');
            var pageToolbar = page.querySelector('[data-wishlist-toolbar]');
            if (pageCount) pageCount.textContent = String(remaining);
            var pageCountText = page.querySelector('[data-wishlist-count-text]');
            if (pageCountText) pageCountText.textContent = wishlistCountText(remaining);
            if (pageToolbar) pageToolbar.hidden = remaining === 0;
            if (!page.querySelector('[data-wishlist-item]')) self.renderPage();
          }
        });
      }
      this.syncBadges();
      this.syncButtons();
    },
    renderPage: function () {
      var page = document.querySelector('[data-wishlist-page]');
      if (!page) return;
      if (isDesignMode) {
        page.removeAttribute('aria-busy');
        page.setAttribute('data-editor-network-paused', 'true');
        return;
      }
      var self = this;
      var grid = page.querySelector('[data-wishlist-grid]');
      var tpl = page.querySelector('template');
      var empty = page.querySelector('[data-wishlist-empty]');
      var toolbar = page.querySelector('[data-wishlist-toolbar]');
      var count = page.querySelector('[data-wishlist-page-count]');
      var handles = this.read();
      if (!grid || !tpl) return;
      grid.innerHTML = '';
      if (count) count.textContent = String(handles.length);
      var countText = page.querySelector('[data-wishlist-count-text]');
      if (countText) countText.textContent = wishlistCountText(handles.length);
      if (toolbar) toolbar.hidden = handles.length === 0;
      if (!handles.length) {
        if (empty) empty.hidden = false;
        return;
      }
      if (empty) empty.hidden = true;
      page.setAttribute('aria-busy', 'true');
      Promise.all(handles.map(function (handle) {
        return fetchJSON(rooted('products/' + encodeURIComponent(handle) + '.js'))
          .catch(function () { return null; });
      })).then(function (products) {
        var valid = products.filter(Boolean);
        var validHandles = valid.map(function (p) { return p.handle; });
        if (validHandles.length !== handles.length) {
          self.write(validHandles);
          self.syncBadges();
        }
        if (count) count.textContent = String(valid.length);
        if (toolbar) toolbar.hidden = valid.length === 0;
        if (empty) empty.hidden = valid.length > 0;

        valid.forEach(function (p) {
          var node = tpl.content.cloneNode(true);
          var item = node.querySelector('[data-wishlist-item]');
          var img = node.querySelector('[data-wl-img]');
          var title = node.querySelector('[data-wl-title]');
          var price = node.querySelector('[data-wl-price]');
          var toggle = node.querySelector('[data-wishlist-toggle]');
          if (item) item.setAttribute('data-handle', p.handle);
          node.querySelectorAll('[data-wl-link]').forEach(function (a) { a.href = p.url; });
          if (img && p.featured_image) { img.src = sizedImage(p.featured_image, 600); img.alt = p.title; }
          else if (img) { img.remove(); }
          if (title) title.textContent = p.title;
          if (price) price.textContent = formatMoney(p.price);
          if (toggle) {
            toggle.setAttribute('data-handle', p.handle);
            toggle.setAttribute('data-wishlist-add-label', (strings.wishlistAdd || '').replace('[product]', p.title));
            toggle.setAttribute('data-wishlist-remove-label', (strings.wishlistRemove || '').replace('[product]', p.title));
          }
          grid.appendChild(node);
        });
        self.syncButtons();
      }).finally(function () {
        page.removeAttribute('aria-busy');
      });
    }
  };

  /* ---------- customer addresses ---------- */

  function initAddressForms() {
    document.querySelectorAll('[data-address-country]').forEach(function (country) {
      if (country.getAttribute('data-address-bound') === 'true') return;
      country.setAttribute('data-address-bound', 'true');
      var form = country.closest('form');
      if (!form) return;
      var province = form.querySelector('[data-address-province]');
      var wrap = form.querySelector('[data-address-province-wrap]');
      if (!province || !wrap) return;

      var countryDefault = country.getAttribute('data-default');
      if (countryDefault) country.value = countryDefault;

      function updateProvinces() {
        var option = country.options[country.selectedIndex];
        var provinces = [];
        try { provinces = JSON.parse(option ? option.getAttribute('data-provinces') || '[]' : '[]'); }
        catch (e) { provinces = []; }

        province.innerHTML = '';
        provinces.forEach(function (entry) {
          var item = document.createElement('option');
          item.value = entry[0];
          item.textContent = entry[1];
          province.appendChild(item);
        });

        wrap.hidden = provinces.length === 0;
        province.disabled = provinces.length === 0;
        var provinceDefault = province.getAttribute('data-default');
        if (provinceDefault && provinces.length) province.value = provinceDefault;
      }

      country.addEventListener('change', updateProvinces);
      updateProvinces();
    });
  }

  /* ---------- progressive form helpers ---------- */

  function initFormHelpers() {
    document.querySelectorAll('[data-auto-submit]').forEach(function (control) {
      if (control.getAttribute('data-auto-submit-bound') === 'true') return;
      control.setAttribute('data-auto-submit-bound', 'true');
      control.addEventListener('change', function () {
        if (!control.form) return;
        if (typeof control.form.requestSubmit === 'function') control.form.requestSubmit();
        else control.form.submit();
      });
    });

    document.querySelectorAll('[data-confirm-submit]').forEach(function (form) {
      if (form.getAttribute('data-confirm-bound') === 'true') return;
      form.setAttribute('data-confirm-bound', 'true');
      form.addEventListener('submit', function (event) {
        var message = form.getAttribute('data-confirm-submit');
        if (message && !window.confirm(message)) event.preventDefault();
      });
    });
  }

  /* ---------- cart page quantity steppers ---------- */

  // The cart page is a plain POST form (`updates[]`), not the AJAX drawer, so the
  // steppers only nudge the sibling input and let the existing [data-auto-submit]
  // listener submit. Bound on the form and matched on an explicit dec/inc value,
  // so the drawer's own valueless [data-cart-qty] buttons can never reach it.
  function initCartPageQty() {
    document.querySelectorAll('form.cart-page').forEach(function (form) {
      if (form.getAttribute('data-cart-qty-bound') === 'true') return;
      form.setAttribute('data-cart-qty-bound', 'true');
      form.addEventListener('click', function (e) {
        var button = e.target.closest('[data-cart-qty="dec"], [data-cart-qty="inc"]');
        if (!button || button.closest('.cart-drawer')) return;
        var control = button.parentNode;
        var input = control ? control.querySelector('input[name="updates[]"]') : null;
        if (!input) return;

        var current = parseInt(input.value, 10);
        if (isNaN(current)) current = 0;
        var next = current + (button.getAttribute('data-cart-qty') === 'inc' ? 1 : -1);
        var min = parseInt(input.getAttribute('min'), 10);
        var max = parseInt(input.getAttribute('max'), 10);
        if (!isNaN(min) && next < min) next = min;
        if (!isNaN(max) && next > max) next = max;
        if (next === current) return;

        input.value = next;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  }

  /* ---------- mobile collection filters ---------- */

  function initCollectionFacets() {
    var facets = document.querySelector('[data-facets]');
    var openButton = document.querySelector('[data-facets-open]');
    if (!facets || !openButton || !window.matchMedia) return;
    if (facets.getAttribute('data-facets-bound') === 'true') return;
    facets.setAttribute('data-facets-bound', 'true');
    var closeButton = facets.querySelector('[data-facets-close]');
    var backdrop = document.querySelector('[data-facets-overlay]');
    var mobile = window.matchMedia('(max-width: 989px)');

    function close(restoreFocus) {
      facets.classList.remove('is-open');
      if (backdrop) backdrop.classList.remove('is-visible');
      openButton.setAttribute('aria-expanded', 'false');
      if (mobile.matches) setDialogState(facets, false);
      document.body.classList.remove('no-scroll');
      if (restoreFocus) openButton.focus();
    }

    function open() {
      if (!mobile.matches) return;
      facets.setAttribute('role', 'dialog');
      facets.setAttribute('aria-modal', 'true');
      setDialogState(facets, true);
      facets.classList.add('is-open');
      if (backdrop) backdrop.classList.add('is-visible');
      openButton.setAttribute('aria-expanded', 'true');
      document.body.classList.add('no-scroll');
      if (closeButton) closeButton.focus();
    }

    function syncMode() {
      if (mobile.matches) {
        facets.setAttribute('role', 'dialog');
        facets.setAttribute('aria-modal', 'true');
        close(false);
      } else {
        facets.classList.remove('is-open');
        if (backdrop) backdrop.classList.remove('is-visible');
        setDialogState(facets, true);
        facets.removeAttribute('aria-hidden');
        facets.removeAttribute('role');
        facets.removeAttribute('aria-modal');
        document.body.classList.remove('no-scroll');
      }
    }

    openButton.addEventListener('click', open);
    if (closeButton) closeButton.addEventListener('click', function () { close(true); });
    if (backdrop) backdrop.addEventListener('click', function () { close(true); });
    facets.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') close(true);
      if (e.key === 'Tab' && mobile.matches) trapFocus(facets, e);
    });
    if (mobile.addEventListener) mobile.addEventListener('change', syncMode);
    syncMode();

    // Empty price inputs would submit as blank filter.v.price.* params and
    // pollute the URL/cache key, so disable them for the duration of submit.
    if (facets.tagName === 'FORM') {
      facets.addEventListener('submit', function () {
        facets.querySelectorAll('input[name^="filter."]').forEach(function (el) {
          if (el.value === '') el.disabled = true;
        });
        setTimeout(function () {
          facets.querySelectorAll('input[name^="filter."]').forEach(function (el) {
            el.disabled = false;
          });
        }, 0);
      });

      // Filters apply themselves: checkboxes submit immediately, price fields
      // on commit (blur/Enter). The manual apply button becomes a no-JS fallback.
      facets.classList.add('facets--auto');
      facets.addEventListener('change', function (e) {
        if (!e.target.matches('input[name^="filter."]')) return;
        if (facets.requestSubmit) facets.requestSubmit(); else facets.submit();
      });
    }
  }

  /* ---------- native Shopify recommendation rails ---------- */

  function initProductRecommendations() {
    document.querySelectorAll('[data-product-recommendations][data-url]').forEach(function (section) {
      if (section.getAttribute('data-recommendations-bound') === 'true') return;
      section.setAttribute('data-recommendations-bound', 'true');
      if (isDesignMode) {
        section.classList.remove('is-loading');
        section.removeAttribute('aria-busy');
        section.setAttribute('data-editor-network-paused', 'true');
        // Replace the endlessly shimmering placeholder with an explanation so
        // the merchant knows the rail only fills on the live storefront.
        var skeleton = section.querySelector('.recommendation-rail__skeleton');
        if (skeleton) {
          var note = document.createElement('div');
          note.className = 'page-width';
          var text = document.createElement('p');
          text.textContent = strings.editorRecommendationsNotice ||
            'Recomandările de produse se încarcă doar pe magazinul live.';
          note.appendChild(text);
          skeleton.parentNode.replaceChild(note, skeleton);
        }
        return;
      }

      function loadRecommendations() {
        if (section.getAttribute('data-recommendations-loading') === 'true') return;
        section.setAttribute('data-recommendations-loading', 'true');

        fetch(section.getAttribute('data-url'), {
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        }).then(function (response) {
          if (!response.ok) throw new Error('Recommendations failed: ' + response.status);
          return response.text();
        }).then(function (html) {
          var shell = document.createElement('div');
          shell.innerHTML = html;
          var rendered = shell.querySelector('[data-product-recommendations]');
          if (!rendered || !rendered.innerHTML.trim()) {
            section.hidden = true;
            return;
          }
          section.innerHTML = rendered.innerHTML;
          section.classList.remove('is-loading');
          section.classList.add('is-loaded');
          section.removeAttribute('aria-busy');
          initProductForms();
          Wishlist.syncButtons();
        }).catch(function () {
          section.hidden = true;
        });
      }

      section.setAttribute('aria-busy', 'true');
      // Eager prefetch instead of scroll-gated loading. Deferring the fetch until
      // the rail neared the viewport saved one request on a bounce, but every
      // visitor who DID scroll paid for it with a visible skeleton wait. Perceived
      // latency is the more expensive currency, so the rail now fetches during the
      // first idle window after binding (capped at 1.5s so a busy main thread can't
      // starve it), with a short timer for browsers lacking requestIdleCallback.
      // Both PDP rails firing within ~1.5s of load is the intended behaviour.
      if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(loadRecommendations, { timeout: 1500 });
      } else {
        window.setTimeout(loadRecommendations, 300);
      }
    });
  }

  function initProductShare() {
    document.querySelectorAll('[data-product-share]').forEach(function (button) {
      if (button.getAttribute('data-share-bound') === 'true') return;
      button.setAttribute('data-share-bound', 'true');
      button.addEventListener('click', function () {
        var title = button.getAttribute('data-share-title') || document.title;
        var status = button.parentElement.querySelector('[data-share-status]');
        var shareData = { title: title, url: window.location.href };

        if (navigator.share) {
          navigator.share(shareData).catch(function () { /* user cancelled */ });
          return;
        }
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(window.location.href).then(function () {
            if (status) {
              status.textContent = strings.shareCopied || 'Link copiat';
              window.setTimeout(function () { status.textContent = ''; }, 2500);
            }
          });
        }
      });
    });
  }

  /* ---------- scroll reveal ---------- */

  function initReveal() {
    if (isDesignMode) {
      document.querySelectorAll('.reveal').forEach(function (el) {
        el.setAttribute('data-reveal-bound', 'true');
        el.classList.add('is-visible');
        el.style.transitionDelay = '0ms';
      });
      return;
    }
    if (!('IntersectionObserver' in window)) {
      document.querySelectorAll('.reveal').forEach(function (el) { el.classList.add('is-visible'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          var el = entry.target;
          var siblings = el.parentElement
            ? Array.prototype.slice.call(el.parentElement.querySelectorAll(':scope > .reveal'))
            : [el];
          var idx = siblings.indexOf(el);
          el.style.transitionDelay = Math.min(idx, 8) * 60 + 'ms';
          el.classList.add('is-visible');
          io.unobserve(el);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });
    document.querySelectorAll('.reveal').forEach(function (el) {
      if (el.getAttribute('data-reveal-bound') === 'true') return;
      el.setAttribute('data-reveal-bound', 'true');
      io.observe(el);
    });
  }

  /* ---------- boot ---------- */

  function bootTheme() {
    [
      initHeader,
      initDesktopMenus,
      initAnnouncement,
      initMobileNav,
      initSearch,
      initHero,
      function () { CartDrawer.init(); },
      initProductForms,
      initPDP,
      initRecentlyViewed,
      function () { Wishlist.init(); Wishlist.renderPage(); },
      initAddressForms,
      initFormHelpers,
      initCartPageQty,
      initCollectionFacets,
      initProductRecommendations,
      initProductShare,
      initReveal
    ].forEach(function (fn) {
      try { fn(); } catch (e) { if (window.console) console.warn('[theme]', e); }
    });
  }

  /* ---------- theme editor lifecycle ---------- */

  // The cart drawer, search modal and wishlist drawer are separate sections that
  // sit off-canvas until opened. Without these handlers a merchant selecting
  // them in the editor sidebar sees nothing at all.
  function editorScope(event) {
    var target = event && event.target;
    if (!target || !target.querySelector) return null;
    return target;
  }

  function openEditorSection(scope) {
    if (!scope) return false;
    if (scope.querySelector('#CartDrawer') || scope.id === 'CartDrawer') {
      CartDrawer.init();
      CartDrawer.open();
      return true;
    }
    var searchModal = scope.querySelector('#SearchModal') || (scope.id === 'SearchModal' ? scope : null);
    if (searchModal && searchModal._csSearchApi) {
      searchModal._csSearchApi.open();
      return true;
    }
    if (scope.querySelector('#WishlistDrawer') || scope.id === 'WishlistDrawer') {
      Wishlist.init();
      Wishlist.open(null);
      return true;
    }
    return false;
  }

  function closeEditorSection(scope) {
    if (!scope) return;
    if (scope.querySelector('#CartDrawer') || scope.id === 'CartDrawer') {
      if (CartDrawer.api) CartDrawer.api.close();
    }
    var searchModal = scope.querySelector('#SearchModal') || (scope.id === 'SearchModal' ? scope : null);
    if (searchModal && searchModal._csSearchApi) searchModal._csSearchApi.close();
    if (scope.querySelector('#WishlistDrawer') || scope.id === 'WishlistDrawer') Wishlist.close();
  }

  function teardownEditorSection(scope) {
    if (!scope) return;
    scope.querySelectorAll('[data-hero]').forEach(function (hero) {
      if (typeof hero._csStop === 'function') hero._csStop();
      if (hero._csVisibility) {
        document.removeEventListener('visibilitychange', hero._csVisibility);
        hero._csVisibility = null;
      }
    });
    scope.querySelectorAll('[data-announcement]').forEach(function (bar) {
      if (bar._csTimer) { window.clearInterval(bar._csTimer); bar._csTimer = null; }
    });
    scope.querySelectorAll('[data-delivery]').forEach(function (box) {
      if (box._csTimer) { window.clearInterval(box._csTimer); box._csTimer = null; }
    });
    if (scope.querySelector('#CartDrawer') || scope.id === 'CartDrawer') {
      CartDrawer.el = null;
    }
  }

  onReady(function () {
    bootTheme();

    if (isDesignMode) {
      if (window.console) {
        console.info('[Cadouri Superbe] Theme Editor safe mode v1.5.2: automatic network requests paused.');
      }
      document.addEventListener('shopify:section:load', function () {
        [
          initHeader,
          initDesktopMenus,
          initAnnouncement,
          initMobileNav,
          initSearch,
          initHero,
          function () { CartDrawer.init(); },
          initProductForms,
          initPDP,
          initRecentlyViewed,
          function () { Wishlist.init(); Wishlist.renderPage(); },
          initAddressForms,
          initFormHelpers,
          initCartPageQty,
          initCollectionFacets,
          initProductRecommendations,
          initProductShare,
          initReveal
        ].forEach(function (fn) {
          try { fn(); } catch (e) { if (window.console) console.warn('[theme editor]', e); }
        });
      });
      document.addEventListener('shopify:section:select', function (event) {
        try { openEditorSection(editorScope(event)); }
        catch (e) { if (window.console) console.warn('[theme editor]', e); }
      });
      document.addEventListener('shopify:section:deselect', function (event) {
        try { closeEditorSection(editorScope(event)); }
        catch (e) { if (window.console) console.warn('[theme editor]', e); }
      });
      document.addEventListener('shopify:section:unload', function (event) {
        try { teardownEditorSection(editorScope(event)); }
        catch (e) { if (window.console) console.warn('[theme editor]', e); }
      });
      document.addEventListener('shopify:block:select', function (event) {
        var slide = event.target && event.target.closest
          ? event.target.closest('[data-hero-slide]')
          : null;
        var hero = slide ? slide.closest('[data-hero]') : null;
        if (hero && typeof hero._csSelectSlide === 'function') hero._csSelectSlide(slide);
        // Search pills live inside the modal, which must be open to be seen.
        var pill = event.target && event.target.closest
          ? event.target.closest('[data-search-pill]')
          : null;
        var modal = pill ? pill.closest('#SearchModal') : null;
        if (modal && modal._csSearchApi) modal._csSearchApi.open();
      });
      document.addEventListener('shopify:block:deselect', function (event) {
        var pill = event.target && event.target.closest
          ? event.target.closest('[data-search-pill]')
          : null;
        var modal = pill ? pill.closest('#SearchModal') : null;
        if (modal && modal._csSearchApi) modal._csSearchApi.close();
      });
    }
  });
})();
